import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import "../styles/global.css";

import { auth, db } from "../firebase";

import { signOut } from "firebase/auth";

import { MdOutlineLightMode, MdModeNight, MdFilterVintage } from "react-icons/md";
import { FaUserCircle } from "react-icons/fa";

import StoryWriter from "../components/StoryWriter";
import DailyChallenge from "../components/Dailychallenge";
import FocusRoom from "../components/FocusRoom";
import {
  doc, getDoc, collection, addDoc, onSnapshot,
  deleteDoc, query, where, Timestamp
} from "firebase/firestore";
import JokesSection from "../components/Jokessection";
import WellnessTests from "../components/Wellnesstests";
import Exercises from "../components/Exercises";

// ─── Nav items ─────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: "stories",   emoji: "📚", label: "Stories"          },
  { id: "breathing", emoji: "🌬️", label: "Breathing"        },
  { id: "challenge", emoji: "🎯", label: "Challenge"        },
  { id: "soulscape", emoji: "🌌", label: "SoulScape"        },
  { id: "gratitude", emoji: "🙏", label: "Gratitude Wall"   },
  { id: "exercises", emoji: "💪", label: "Exercises"        },
  { id: "test",      emoji: "📝", label: "Assessments"       },
  { id: "games",     emoji: "🎮", label: "Mini Games"       },
  { id: "jokes",     emoji: "😂", label: "Jokes"            },
  { id: "roadmap",   emoji: "🗺️", label: "Recovery Roadmap" },
];

// ─── 35 Motivational Quotes ────────────────────────────────────────────────────
const MOTIVATIONAL_QUOTES = [
  { text: "You don't have to be great to start, but you have to start to be great.", author: "Zig Ziglar" },
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
  { text: "It always seems impossible until it's done.", author: "Nelson Mandela" },
  { text: "You are braver than you believe, stronger than you seem, and smarter than you think.", author: "A.A. Milne" },
  { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { text: "In the middle of every difficulty lies opportunity.", author: "Albert Einstein" },
  { text: "What you get by achieving your goals is not as important as what you become.", author: "Thoreau" },
  { text: "Life is 10% what happens to you and 90% how you react to it.", author: "Charles R. Swindoll" },
  { text: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt" },
  { text: "It is during our darkest moments that we must focus to see the light.", author: "Aristotle" },
  { text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { text: "Keep your face always toward the sunshine and shadows will fall behind you.", author: "Walt Whitman" },
  { text: "You are enough. You have enough. You do enough.", author: "Unknown" },
  { text: "Every day is a second chance.", author: "Unknown" },
  { text: "Small steps every day add up to miles over time.", author: "Unknown" },
  { text: "Progress, not perfection.", author: "Unknown" },
  { text: "The comeback is always stronger than the setback.", author: "Unknown" },
  { text: "Hardships often prepare ordinary people for an extraordinary destiny.", author: "C.S. Lewis" },
  { text: "You have been assigned this mountain to show others it can be moved.", author: "Unknown" },
  { text: "Fall seven times, stand up eight.", author: "Japanese Proverb" },
  { text: "What lies behind us and what lies before us are tiny matters compared to what lies within us.", author: "Emerson" },
  { text: "The only person you are destined to become is the person you decide to be.", author: "Emerson" },
  { text: "Breathe. You've survived everything so far.", author: "Unknown" },
  { text: "Healing is not linear. Be gentle with yourself.", author: "Unknown" },
  { text: "One day at a time. One step at a time.", author: "Unknown" },
  { text: "You deserve the same compassion you give to others.", author: "Unknown" },
  { text: "Rest if you must, but don't quit.", author: "Unknown" },
  { text: "The mind is everything. What you think, you become.", author: "Buddha" },
  { text: "With the new day comes new strength and new thoughts.", author: "Eleanor Roosevelt" },
  { text: "Start where you are. Use what you have. Do what you can.", author: "Arthur Ashe" },
  { text: "Even the darkest night will end and the sun will rise.", author: "Victor Hugo" },
  { text: "You are worthy of love and belonging, exactly as you are.", author: "Brené Brown" },
  { text: "The root of joy is gratefulness.", author: "David Steindl-Rast" },
  { text: "Your only limit is your mind.", author: "Unknown" },
];

// ─── Word Scramble Data — 220+ diverse general words across 20 levels ─────────
const WORD_BANK = [
  { word: "bird", hint: "A feathered creature that can fly", level: 1 },
  { word: "frog", hint: "Green amphibian that jumps", level: 1 },
  { word: "lamp", hint: "A device that gives light", level: 1 },
  { word: "drum", hint: "A percussion instrument you beat", level: 1 },
  { word: "crab", hint: "Crustacean with claws that walks sideways", level: 1 },
  { word: "gold", hint: "Precious yellow metal", level: 1 },
  { word: "wolf", hint: "Wild relative of the dog", level: 1 },
  { word: "cake", hint: "Sweet baked treat for celebrations", level: 1 },
  { word: "star", hint: "Shining body in the night sky", level: 1 },
  { word: "fish", hint: "Aquatic animal with fins and gills", level: 1 },
  { word: "tiger", hint: "Striped big cat from Asia", level: 2 },
  { word: "pizza", hint: "Italian dish with cheese and toppings", level: 2 },
  { word: "cloud", hint: "White or grey mass in the sky", level: 2 },
  { word: "piano", hint: "Large keyboard musical instrument", level: 2 },
  { word: "ocean", hint: "Vast body of salt water", level: 2 },
  { word: "brush", hint: "Tool with bristles for painting or hair", level: 2 },
  { word: "eagle", hint: "Large bird of prey with sharp talons", level: 2 },
  { word: "maple", hint: "Tree famous for its syrup", level: 2 },
  { word: "gecko", hint: "Small lizard that can climb walls", level: 2 },
  { word: "torch", hint: "Portable handheld light source", level: 2 },
  { word: "shark", hint: "Predatory fish with rows of teeth", level: 3 },
  { word: "flute", hint: "Woodwind instrument you blow sideways", level: 3 },
  { word: "denim", hint: "Blue fabric jeans are made from", level: 3 },
  { word: "llama", hint: "South American animal related to camel", level: 3 },
  { word: "solar", hint: "Relating to the sun", level: 3 },
  { word: "tulip", hint: "Spring flower with cup-shaped bloom", level: 3 },
  { word: "prism", hint: "Glass shape that splits light into colours", level: 3 },
  { word: "koala", hint: "Fluffy Australian marsupial in eucalyptus trees", level: 3 },
  { word: "atlas", hint: "A book of maps", level: 3 },
  { word: "coral", hint: "Ocean reef made by tiny marine animals", level: 3 },
  { word: "cactus", hint: "Desert plant with sharp spines", level: 4 },
  { word: "magnet", hint: "Object that attracts iron and steel", level: 4 },
  { word: "falcon", hint: "Fast diving bird of prey", level: 4 },
  { word: "walnut", hint: "Wrinkled nut inside a hard shell", level: 4 },
  { word: "frozen", hint: "Turned to ice by cold", level: 4 },
  { word: "rocket", hint: "Vehicle launched into space", level: 4 },
  { word: "pebble", hint: "Small smooth stone", level: 4 },
  { word: "radish", hint: "Small red or white root vegetable", level: 4 },
  { word: "jigsaw", hint: "Puzzle made of interlocking pieces", level: 4 },
  { word: "cobalt", hint: "Vivid blue metallic element", level: 4 },
  { word: "butter", hint: "Dairy spread made from cream", level: 5 },
  { word: "island", hint: "Land entirely surrounded by water", level: 5 },
  { word: "fossil", hint: "Preserved remains of ancient life", level: 5 },
  { word: "cherry", hint: "Small round red or dark fruit", level: 5 },
  { word: "statue", hint: "Carved or moulded figure of a person or animal", level: 5 },
  { word: "pepper", hint: "Common spice used in cooking, can be hot", level: 5 },
  { word: "budget", hint: "Plan for how to spend money", level: 5 },
  { word: "lizard", hint: "Scaly reptile with four legs and a long tail", level: 5 },
  { word: "bridge", hint: "Structure that crosses a gap or river", level: 5 },
  { word: "goblin", hint: "Mischievous creature from folklore", level: 5 },
  { word: "dolphin", hint: "Intelligent marine mammal known for leaping", level: 6 },
  { word: "cabinet", hint: "Storage furniture with shelves or drawers", level: 6 },
  { word: "blanket", hint: "Warm covering used in bed", level: 6 },
  { word: "captain", hint: "Leader of a ship, plane, or sports team", level: 6 },
  { word: "channel", hint: "A TV station or a narrow waterway", level: 6 },
  { word: "fiction", hint: "Stories that are imagined, not real", level: 6 },
  { word: "penguin", hint: "Flightless seabird in tuxedo-like colouring", level: 6 },
  { word: "mustard", hint: "Yellow condiment made from seeds", level: 6 },
  { word: "spinner", hint: "Something that rotates around a center", level: 6 },
  { word: "tornado", hint: "Violent rotating column of air", level: 6 },
  { word: "mystery", hint: "Something unexplained or unknown", level: 7 },
  { word: "compass", hint: "Tool used to find direction using north", level: 7 },
  { word: "lobster", hint: "Large red crustacean with claws", level: 7 },
  { word: "cheddar", hint: "Popular type of yellow cheese", level: 7 },
  { word: "volcano", hint: "Mountain that can erupt with lava", level: 7 },
  { word: "drought", hint: "Long period of very little rainfall", level: 7 },
  { word: "lantern", hint: "Portable container enclosing a light", level: 7 },
  { word: "journal", hint: "A personal diary or scientific publication", level: 7 },
  { word: "panther", hint: "Large black wild cat", level: 7 },
  { word: "keyboard", hint: "Input device with keys for typing", level: 8 },
  { word: "calendar", hint: "Chart showing the days and months", level: 8 },
  { word: "avocado", hint: "Green creamy fruit used in guacamole", level: 8 },
  { word: "mushroom", hint: "Fungus with a cap, some edible some not", level: 8 },
  { word: "treasure", hint: "Collection of valuable items or hidden riches", level: 8 },
  { word: "scissors", hint: "Cutting tool with two blades joined in the middle", level: 8 },
  { word: "platform", hint: "Raised level surface or digital system", level: 8 },
  { word: "notebook", hint: "Small book for writing notes", level: 8 },
  { word: "chlorine", hint: "Chemical element used to clean pools", level: 8 },
  { word: "dinosaur", hint: "Ancient reptile that went extinct 65 million years ago", level: 8 },
  { word: "corridor", hint: "A long hallway in a building", level: 9 },
  { word: "broccoli", hint: "Green vegetable shaped like a tree", level: 9 },
  { word: "skeleton", hint: "Framework of bones in a body", level: 9 },
  { word: "software", hint: "Computer programs and operating systems", level: 9 },
  { word: "snowflake", hint: "Unique crystal of frozen water falling from sky", level: 9 },
  { word: "magazine", hint: "Periodical publication with articles and photos", level: 9 },
  { word: "triangle", hint: "Shape with three sides and three angles", level: 9 },
  { word: "molecule", hint: "Smallest unit of a substance with its properties", level: 9 },
  { word: "workshop", hint: "Room or event for hands-on learning or making", level: 9 },
  { word: "flagship", hint: "Most important product or ship in a fleet", level: 9 },
  { word: "algorithm", hint: "Step-by-step set of rules to solve a problem", level: 10 },
  { word: "jellyfish", hint: "Transparent ocean creature with tentacles", level: 10 },
  { word: "champagne", hint: "Sparkling wine from a region of France", level: 10 },
  { word: "chocolate", hint: "Sweet food made from cacao beans", level: 10 },
  { word: "parachute", hint: "Device used to slow a fall from the sky", level: 10 },
  { word: "clockwise", hint: "Turning in the same direction as clock hands", level: 10 },
  { word: "crocodile", hint: "Large reptile with powerful jaws living near water", level: 10 },
  { word: "blueprint", hint: "Technical drawing plan; a detailed design", level: 10 },
  { word: "magnitude", hint: "The size or extent of something", level: 10 },
  { word: "porcupine", hint: "Animal covered in sharp protective quills", level: 10 },
  { word: "superimposition", hint: "Placing one image or thing on top of another", level: 20 },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────
function Tag({ label }) {
  return (
    <span style={{
      display:"inline-block", padding:"3px 11px",
      borderRadius:"var(--radius-full)", fontSize:"0.72rem", fontWeight:700,
      background:"var(--primary-light)", color:"var(--primary-color)",
      letterSpacing:"0.03em", marginBottom:10,
    }}>{label}</span>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="card animate-in" style={{ marginTop:0 }}>
      <h2 style={{ marginBottom:24 }}>{title}</h2>
      {children}
    </div>
  );
}

// ─── Random Quote Card ─────────────────────────────────────────────────────────
function DailyQuoteCard() {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length));
  const [animating, setAnimating] = useState(false);

  function nextQuote() {
    setAnimating(true);
    setTimeout(() => {
      setIdx(i => (i + 1) % MOTIVATIONAL_QUOTES.length);
      setAnimating(false);
    }, 300);
  }

  const q = MOTIVATIONAL_QUOTES[idx];

  return (
    <div style={{
      borderRadius: 20,
      background: "linear-gradient(135deg, var(--primary-light,#EDE9FE) 0%, var(--secondary-light,#FFF0F7) 100%)",
      border: "1.5px solid var(--primary-color,#7C4DFF)20",
      padding: "24px 28px",
      marginBottom: 28,
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 12, left: 20,
        fontSize: "5rem", lineHeight: 1, color: "var(--primary-color,#7C4DFF)",
        opacity: 0.08, fontFamily: "Georgia, serif", userSelect: "none",
        pointerEvents: "none",
      }}>"</div>

      <div style={{
        transition: "opacity 0.3s ease, transform 0.3s ease",
        opacity: animating ? 0 : 1,
        transform: animating ? "translateY(8px)" : "translateY(0)",
      }}>
        <p style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: "clamp(1rem, 2vw, 1.18rem)",
          lineHeight: 1.6,
          color: "var(--text-color,#111)",
          fontStyle: "italic",
          margin: "0 0 12px",
          paddingLeft: 8,
        }}>
          "{q.text}"
        </p>
        <p style={{
          fontSize: "0.8rem",
          fontWeight: 700,
          color: "var(--primary-color,#7C4DFF)",
          margin: 0,
          paddingLeft: 8,
        }}>— {q.author}</p>
      </div>

      <button
        onClick={nextQuote}
        style={{
          position: "absolute", bottom: 16, right: 20,
          background: "var(--primary-color,#7C4DFF)",
          color: "#fff", border: "none", borderRadius: 10,
          padding: "6px 14px", fontSize: "0.72rem", fontWeight: 700,
          cursor: "pointer", opacity: 0.85, transition: "opacity 0.2s",
          letterSpacing: "0.03em",
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = "1"}
        onMouseLeave={e => e.currentTarget.style.opacity = "0.85"}
      >
        ✨ New Quote
      </button>
    </div>
  );
}

// ─── SoulScape Fullscreen Overlay ─────────────────────────────────────────────
function SoulScapeOverlay({ onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      animation: "soulscapeIn 0.45s cubic-bezier(0.22,1,0.36,1) forwards",
    }}>
      <style>{`
        @keyframes soulscapeIn {
          from { opacity: 0; transform: scale(0.985); }
          to   { opacity: 1; transform: scale(1); }
        }
        .soulscape-back-btn {
          position: fixed;
          top: 18px;
          left: 18px;
          z-index: 10001;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 18px;
          border-radius: 50px;
          border: 1.5px solid rgba(255,255,255,0.3);
          background: rgba(0,0,0,0.35);
          color: rgba(255,255,255,0.92);
          font-size: 0.82rem;
          font-weight: 700;
          cursor: pointer;
          letter-spacing: 0.04em;
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          transition: background 0.2s, border-color 0.2s, transform 0.15s;
          font-family: 'DM Sans', sans-serif;
          box-shadow: 0 4px 16px rgba(0,0,0,0.3);
        }
        .soulscape-back-btn:hover {
          background: rgba(0,0,0,0.6);
          border-color: rgba(255,255,255,0.55);
          transform: translateX(-2px);
        }
        .soulscape-back-btn .arrow {
          font-size: 1rem;
          transition: transform 0.2s;
        }
        .soulscape-back-btn:hover .arrow {
          transform: translateX(-3px);
        }
        .soulscape-wrapper {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }
        .soulscape-wrapper [data-fullscreen-toggle] {
          display: none !important;
        }
      `}</style>

      <button className="soulscape-back-btn" onClick={onClose}>
        <span className="arrow">←</span>
        <span>Back to Resources</span>
      </button>

      <div className="soulscape-wrapper">
        <FocusRoom isEmbeddedFullscreen={true} />
      </div>
    </div>
  );
}

// ─── AI Tic Tac Toe ────────────────────────────────────────────────────────────
function TicTacToe() {
  const [board, setBoard]           = useState(Array(9).fill(null));
  const [playerTurn, setPlayerTurn] = useState(true);
  const [status, setStatus]         = useState("Your turn (X)");
  const [scores, setScores]         = useState({ X:0, O:0, draws:0 });
  const [thinking, setThinking]     = useState(false);

  const lines = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];

  function getWinner(b) {
    for (const [a,bb,c] of lines)
      if (b[a] && b[a]===b[bb] && b[a]===b[c]) return b[a];
    return null;
  }

  function minimax(b, isMax) {
    const w = getWinner(b);
    if (w === "O") return 10;
    if (w === "X") return -10;
    if (b.every(Boolean)) return 0;
    if (isMax) {
      let best = -Infinity;
      b.forEach((cell, i) => { if (!cell) { b[i]="O"; best=Math.max(best,minimax(b,false)); b[i]=null; } });
      return best;
    } else {
      let best = Infinity;
      b.forEach((cell, i) => { if (!cell) { b[i]="X"; best=Math.min(best,minimax(b,true)); b[i]=null; } });
      return best;
    }
  }

  function bestMove(b) {
    let bestVal=-Infinity, bestIdx=-1;
    b.forEach((cell,i)=>{ if(!cell){ b[i]="O"; const val=minimax(b,false); b[i]=null; if(val>bestVal){bestVal=val;bestIdx=i;} } });
    return bestIdx;
  }

  function aiMove(b) {
    const empty = b.map((c,i)=>!c?i:null).filter(i=>i!==null);
    if (empty.length === 0) return -1;
    for (const i of empty) {
      const test = [...b]; test[i] = "O";
      if (getWinner(test)) return i;
    }
    for (const i of empty) {
      const test = [...b]; test[i] = "X";
      if (getWinner(test) && Math.random() < 0.6) return i;
    }
    if (Math.random() < 0.5) {
      return bestMove(b);
    } else {
      return empty[Math.floor(Math.random() * empty.length)];
    }
  }

  function handleClick(i) {
    if (!playerTurn||board[i]||getWinner(board)||thinking) return;
    const nb=[...board]; nb[i]="X";
    const w=getWinner(nb);
    if(w){setBoard(nb);setStatus("🎉 You win!");setScores(s=>({...s,X:s.X+1}));setPlayerTurn(false);return;}
    if(nb.every(Boolean)){setBoard(nb);setStatus("🤝 Draw!");setScores(s=>({...s,draws:s.draws+1}));setPlayerTurn(false);return;}
    setBoard(nb);setPlayerTurn(false);setThinking(true);setStatus("🤖 AI thinking…");
    setTimeout(()=>{
      const nb2=[...nb]; const ai=aiMove(nb2); if(ai===-1) return; nb2[ai]="O";
      const w2=getWinner(nb2);
      if(w2){setBoard(nb2);setStatus("🤖 AI wins!");setScores(s=>({...s,O:s.O+1}));setThinking(false);return;}
      if(nb2.every(Boolean)){setBoard(nb2);setStatus("🤝 Draw!");setScores(s=>({...s,draws:s.draws+1}));setThinking(false);return;}
      setBoard(nb2);setPlayerTurn(true);setStatus("Your turn (X)");setThinking(false);
    },450);
  }

  function reset(){ setBoard(Array(9).fill(null));setPlayerTurn(true);setStatus("Your turn (X)");setThinking(false); }

  const winner=getWinner(board);
  const winLine=winner?lines.find(([a,b,c])=>board[a]&&board[a]===board[b]&&board[a]===board[c]):null;

  return (
    <div style={{textAlign:"center"}}>
      <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:16}}>
        {[["You (X)",scores.X,"var(--primary-color)","var(--primary-light)"],
          ["Draw",scores.draws,"var(--text-muted)","var(--bg-secondary)"],
          ["AI (O)",scores.O,"var(--secondary-color)","var(--secondary-light)"]].map(([lbl,val,c,bg])=>(
          <div key={lbl} style={{background:bg,borderRadius:"var(--radius-md)",padding:"10px 18px",minWidth:70}}>
            <div style={{fontSize:"1.4rem",fontWeight:800,color:c,fontFamily:"'DM Serif Display', serif"}}>{val}</div>
            <div style={{fontSize:"0.72rem",fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase"}}>{lbl}</div>
          </div>
        ))}
      </div>
      <p style={{fontWeight:600,color:"var(--text-secondary-color)",marginBottom:12,minHeight:24}}>{status}</p>
      <div className="tic-grid" style={{justifyContent:"center"}}>
        {board.map((cell,i)=>{
          const inWinLine=winLine?.includes(i);
          return (
            <button key={i} className="tic-cell" onClick={()=>handleClick(i)} style={{
              color:cell==="X"?"var(--primary-color)":"var(--secondary-color)",fontWeight:800,
              background:inWinLine?(cell==="X"?"var(--primary-light)":"var(--secondary-light)"):undefined,
              borderColor:inWinLine?(cell==="X"?"var(--primary-color)":"var(--secondary-color)"):undefined,
              transform:inWinLine?"scale(1.08)":undefined,transition:"all 0.2s ease",
              cursor:(!cell&&playerTurn&&!winner)?"pointer":"default",
            }}>{cell}</button>
          );
        })}
      </div>
      <p style={{fontSize:"0.75rem",color:"var(--text-muted)",marginTop:8}}>AI difficulty: Balanced (beatable!)</p>
      <button className="btn secondary-btn" onClick={reset} style={{marginTop:8}}>New Game</button>
    </div>
  );
}

// ─── Number Guessing Game ──────────────────────────────────────────────────────
function NumberGuess() {
  const [target]              = useState(()=>Math.floor(Math.random()*100)+1);
  const [guess, setGuess]     = useState("");
  const [history, setHistory] = useState([]);
  const [won, setWon]         = useState(false);

  function submit() {
    const n=parseInt(guess);
    if(isNaN(n)||n<1||n>100) return;
    let hint="";
    if(n===target){hint="🎉 Correct!";setWon(true);}
    else if(n<target) hint=n<target-20?"📈 Way too low!":"📈 A bit low";
    else hint=n>target+20?"📉 Way too high!":"📉 A bit high";
    setHistory(h=>[{n,hint},...h]); setGuess("");
  }

  return (
    <div className="game-wrapper" style={{textAlign:"center"}}>
      <p style={{color:"var(--text-muted)",fontSize:"0.875rem",marginBottom:20}}>
        Guess a number between <strong>1 – 100</strong>. Unlimited tries!
      </p>
      {!won?(
        <div style={{display:"flex",gap:10,justifyContent:"center",marginBottom:20}}>
          <input type="number" min={1} max={100} value={guess}
            onChange={e=>setGuess(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}
            placeholder="Your guess…" style={{width:140,textAlign:"center",fontSize:"1.1rem"}}/>
          <button className="btn primary-btn" onClick={submit}>Guess</button>
        </div>
      ):(
        <div>
          <p style={{fontSize:"1.4rem",fontFamily:"'DM Serif Display', serif",color:"var(--primary-color)",marginBottom:8}}>
            🎉 Got it in {history.length} {history.length===1?"try":"tries"}!
          </p>
          <button className="btn primary-btn" onClick={()=>window.location.reload()} style={{marginBottom:16}}>Play Again</button>
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:180,overflowY:"auto"}}>
        {history.map((h,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
            background:"var(--card-bg-secondary)",border:"1px solid var(--border-subtle)",
            borderRadius:"var(--radius-md)",padding:"8px 16px",fontSize:"0.9rem"}}>
            <span style={{fontWeight:700,color:"var(--text-color)"}}>→ {h.n}</span>
            <span style={{color:"var(--text-secondary-color)"}}>{h.hint}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Memory Match ──────────────────────────────────────────────────────────────
function MemoryMatch() {
  const EMOJIS=["🌸","🦋","🎯","🍀","⭐","🌙","🔥","💎"];
  function newDeck(){ return [...EMOJIS,...EMOJIS].sort(()=>Math.random()-0.5).map((e,i)=>({id:i,emoji:e,flipped:false,matched:false})); }
  const [cards,setCards]     = useState(newDeck);
  const [flipped,setFlipped] = useState([]);
  const [moves,setMoves]     = useState(0);
  const [locked,setLocked]   = useState(false);
  const allMatched=cards.every(c=>c.matched);

  function flip(id){
    if(locked||flipped.length===2) return;
    const card=cards.find(c=>c.id===id);
    if(!card||card.flipped||card.matched) return;
    const newFlipped=[...flipped,id];
    setCards(cs=>cs.map(c=>c.id===id?{...c,flipped:true}:c)); setFlipped(newFlipped);
    if(newFlipped.length===2){
      setMoves(m=>m+1); setLocked(true);
      const [a,b]=newFlipped.map(fid=>cards.find(c=>c.id===fid));
      if(a.emoji===b.emoji){setCards(cs=>cs.map(c=>newFlipped.includes(c.id)?{...c,matched:true}:c));setFlipped([]);setLocked(false);}
      else{setTimeout(()=>{setCards(cs=>cs.map(c=>newFlipped.includes(c.id)?{...c,flipped:false}:c));setFlipped([]);setLocked(false);},900);}
    }
  }

  return (
    <div style={{textAlign:"center"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <span style={{color:"var(--text-muted)",fontSize:"0.875rem"}}>Moves: <strong style={{color:"var(--text-color)"}}>{moves}</strong></span>
        {allMatched?<span style={{color:"var(--primary-color)",fontWeight:700}}>🎉 Solved in {moves} moves!</span>:<span style={{color:"var(--text-muted)",fontSize:"0.875rem"}}>Find all pairs</span>}
        <button className="btn secondary-btn" style={{padding:"6px 14px",fontSize:"0.8rem"}} onClick={()=>{setCards(newDeck());setFlipped([]);setMoves(0);setLocked(false);}}>Reset</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,maxWidth:320,margin:"0 auto"}}>
        {cards.map(card=>(
          <button key={card.id} onClick={()=>flip(card.id)} style={{
            height:72,fontSize:"1.6rem",borderRadius:"var(--radius-md)",
            border:card.matched?"2px solid var(--secondary-color)":"1.5px solid var(--border-color)",
            background:card.matched?"var(--secondary-light)":card.flipped?"var(--primary-light)":"var(--bg-secondary)",
            cursor:card.matched||card.flipped?"default":"pointer",transition:"all 0.2s ease",
          }}>{card.flipped||card.matched?card.emoji:""}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Word Scramble ─────────────────────────────────────────────────────────────
function WordScramble() {
  const WORDS_PER_LEVEL = 5;
  const MAX_LEVEL = 20;

  const [level, setLevel]           = useState(1);
  const [levelWords, setLevelWords] = useState([]);
  const [wordIdx, setWordIdx]       = useState(0);
  const [scrambled, setScrambled]   = useState("");
  const [input, setInput]           = useState("");
  const [result, setResult]         = useState(null);
  const [score, setScore]           = useState(0);
  const [levelComplete, setLevelComplete] = useState(false);
  const [streak, setStreak]         = useState(0);
  const [showHint, setShowHint]     = useState(false);

  function scramble(w) {
    const arr = w.replace(/-/g,"").split("");
    let s;
    do { s = [...arr].sort(()=>Math.random()-0.5).join(""); } while (s === w.replace(/-/g,"") && arr.length > 1);
    return s;
  }

  function loadLevel(lvl) {
    const pool = WORD_BANK.filter(w => w.level === lvl);
    const shuffled = [...pool].sort(()=>Math.random()-0.5).slice(0, WORDS_PER_LEVEL);
    setLevelWords(shuffled);
    setWordIdx(0);
    setLevelComplete(false);
    setInput("");
    setResult(null);
    setShowHint(false);
    return shuffled;
  }

  useEffect(() => { loadLevel(level); }, [level]);

  useEffect(() => {
    if (levelWords.length > 0 && levelWords[wordIdx]) {
      setScrambled(scramble(levelWords[wordIdx].word));
      setInput("");
      setResult(null);
      setShowHint(false);
    }
  }, [wordIdx, levelWords]);

  function check() {
    if (!levelWords[wordIdx]) return;
    const cleanWord = levelWords[wordIdx].word.replace(/-/g,"");
    if (input.trim().toLowerCase() === cleanWord || input.trim().toLowerCase() === levelWords[wordIdx].word) {
      setResult("correct");
      setScore(s => s + 10 * level);
      setStreak(s => s + 1);
    } else {
      setResult("wrong");
      setStreak(0);
    }
  }

  function next() {
    const nextIdx = wordIdx + 1;
    if (nextIdx >= WORDS_PER_LEVEL) { setLevelComplete(true); }
    else { setWordIdx(nextIdx); }
  }

  const currentWord = levelWords[wordIdx];
  const difficultyLabel = level <= 5 ? "Easy" : level <= 10 ? "Medium" : level <= 15 ? "Hard" : "Expert";
  const difficultyColor = level <= 5 ? "var(--secondary-color)" : level <= 10 ? "var(--accent-color)" : level <= 15 ? "var(--primary-color)" : "#e53e3e";

  if (levelComplete) {
    return (
      <div className="game-wrapper" style={{textAlign:"center",padding:"20px 0"}}>
        <div style={{fontSize:52,marginBottom:12}}>🏆</div>
        <p style={{fontFamily:"'DM Serif Display', serif",fontSize:"1.6rem",color:"var(--primary-color)",marginBottom:6}}>Level {level} Complete!</p>
        <p style={{color:"var(--text-muted)",marginBottom:4}}>You unscrambled all {WORDS_PER_LEVEL} words!</p>
        <p style={{fontWeight:700,color:"var(--text-color)",marginBottom:20}}>Total Score: <span style={{color:"var(--primary-color)"}}>{score}</span></p>
        {level < MAX_LEVEL ? (
          <button className="btn primary-btn" onClick={()=>setLevel(l=>l+1)}>Level {level + 1} →</button>
        ) : (
          <div>
            <p style={{color:"var(--secondary-color)",fontWeight:700,fontSize:"1.1rem",marginBottom:12}}>🎉 You've mastered all 20 levels!</p>
            <button className="btn secondary-btn" onClick={()=>{setLevel(1);setScore(0);setStreak(0);}}>Play Again</button>
          </div>
        )}
      </div>
    );
  }

  if (!currentWord) return null;

  return (
    <div className="game-wrapper" style={{textAlign:"center"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{textAlign:"left"}}>
          <div style={{fontSize:"0.7rem",fontWeight:700,textTransform:"uppercase",color:"var(--text-muted)"}}>Level</div>
          <div style={{fontWeight:800,color:"var(--primary-color)",fontSize:"1.1rem"}}>{level} <span style={{fontSize:"0.7rem",color:difficultyColor,fontWeight:700}}>({difficultyLabel})</span></div>
        </div>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:"0.7rem",fontWeight:700,textTransform:"uppercase",color:"var(--text-muted)"}}>Word</div>
          <div style={{fontWeight:700,color:"var(--text-color)"}}>{wordIdx + 1} / {WORDS_PER_LEVEL}</div>
        </div>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:"0.7rem",fontWeight:700,textTransform:"uppercase",color:"var(--text-muted)"}}>Streak 🔥</div>
          <div style={{fontWeight:800,color:"var(--accent-color)"}}>{streak}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:"0.7rem",fontWeight:700,textTransform:"uppercase",color:"var(--text-muted)"}}>Score</div>
          <div style={{fontWeight:700,color:"var(--primary-color)"}}>{score}</div>
        </div>
      </div>
      <div style={{display:"flex",gap:4,marginBottom:20}}>
        {Array.from({length:WORDS_PER_LEVEL}).map((_,i)=>(
          <div key={i} style={{flex:1,height:5,borderRadius:3,background:i<wordIdx?"var(--primary-color)":i===wordIdx?"var(--accent-color)":"var(--border-color)",transition:"background 0.3s"}}/>
        ))}
      </div>
      <p style={{color:"var(--text-muted)",fontSize:"0.875rem",marginBottom:8}}>
        Hint: {showHint ? currentWord.hint : <span style={{cursor:"pointer",color:"var(--primary-color)",textDecoration:"underline"}} onClick={()=>setShowHint(true)}>Show hint</span>}
      </p>
      <div style={{fontFamily:"'DM Serif Display', serif",fontSize:"clamp(1.6rem,4vw,2.2rem)",letterSpacing:"0.3em",color:"var(--text-color)",marginBottom:20,padding:"16px 24px",background:"var(--bg-secondary)",borderRadius:"var(--radius-md)",display:"inline-block",minWidth:200}}>
        {scrambled}
      </div>
      <div style={{fontSize:"0.75rem",color:"var(--text-muted)",marginBottom:16}}>{currentWord.word.length} letters</div>
      <div style={{display:"flex",gap:10,justifyContent:"center",marginBottom:14}}>
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&(result==="correct"?next():check())}
          placeholder="Unscramble it…" style={{width:220,textAlign:"center"}} autoFocus/>
        <button className="btn primary-btn" onClick={check} disabled={result==="correct"}>Check</button>
      </div>
      {result==="correct" && (
        <div style={{marginBottom:12}}>
          <p style={{color:"var(--secondary-color)",fontWeight:700,marginBottom:8}}>✅ Correct! {streak > 1 ? `🔥 ${streak}x streak!` : ""} +{10 * level} pts</p>
          <button className="btn primary-btn" onClick={next}>{wordIdx + 1 >= WORDS_PER_LEVEL ? "Finish Level 🏆" : "Next Word →"}</button>
        </div>
      )}
      {result==="wrong" && (<p style={{color:"var(--primary-color)",fontWeight:700,marginBottom:12}}>❌ Try again!</p>)}
      <div style={{marginTop:20,borderTop:"1px solid var(--border-color)",paddingTop:16}}>
        <p style={{fontSize:"0.75rem",color:"var(--text-muted)",marginBottom:8}}>Jump to level:</p>
        <div style={{display:"flex",flexWrap:"wrap",gap:4,justifyContent:"center"}}>
          {Array.from({length:MAX_LEVEL}).map((_,i)=>(
            <button key={i} onClick={()=>setLevel(i+1)} style={{
              width:30,height:30,fontSize:"0.7rem",fontWeight:700,borderRadius:"var(--radius-sm)",
              border:"1.5px solid var(--border-color)",cursor:"pointer",
              background:level===i+1?"var(--primary-color)":"var(--bg-secondary)",
              color:level===i+1?"white":"var(--text-secondary-color)",
            }}>{i+1}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Snake Game ────────────────────────────────────────────────────────────────
function SnakeGame() {
  const GRID = 20;
  const CELL = 18;
  const initSnake = [{x:10,y:10},{x:9,y:10},{x:8,y:10}];
  const [snake, setSnake]     = useState(initSnake);
  const [food, setFood]       = useState({x:15,y:10});
  const [dir, setDir]         = useState({x:1,y:0});
  const [running, setRunning] = useState(false);
  const [dead, setDead]       = useState(false);
  const [score, setScore]     = useState(0);
  const [best, setBest]       = useState(0);
  const dirRef                = useRef({x:1,y:0});
  const snakeRef              = useRef(initSnake);
  const foodRef               = useRef({x:15,y:10});
  const deadRef               = useRef(false);

  function randFood(s) {
    let f;
    do { f = {x:Math.floor(Math.random()*GRID),y:Math.floor(Math.random()*GRID)}; }
    while (s.some(seg=>seg.x===f.x&&seg.y===f.y));
    return f;
  }

  useEffect(() => {
    const handleKey = e => {
      const map = {ArrowUp:{x:0,y:-1},ArrowDown:{x:0,y:1},ArrowLeft:{x:-1,y:0},ArrowRight:{x:1,y:0},
                   w:{x:0,y:-1},s:{x:0,y:1},a:{x:-1,y:0},d:{x:1,y:0}};
      const nd = map[e.key];
      if (nd && !(nd.x===-dirRef.current.x&&nd.y===0) && !(nd.y===-dirRef.current.y&&nd.x===0)) {
        dirRef.current = nd; setDir(nd);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      if (deadRef.current) return;
      const s = snakeRef.current;
      const d = dirRef.current;
      const head = {x:(s[0].x+d.x+GRID)%GRID, y:(s[0].y+d.y+GRID)%GRID};
      if (s.some(seg=>seg.x===head.x&&seg.y===head.y)) {
        deadRef.current = true;
        setDead(true); setRunning(false);
        setScore(sc => { setBest(b=>Math.max(b,sc)); return sc; });
        return;
      }
      let newSnake;
      if (head.x===foodRef.current.x && head.y===foodRef.current.y) {
        newSnake = [head,...s];
        const nf = randFood(newSnake);
        foodRef.current = nf; setFood(nf);
        setScore(sc => sc + 10);
      } else {
        newSnake = [head,...s.slice(0,-1)];
      }
      snakeRef.current = newSnake; setSnake([...newSnake]);
    }, 130);
    return () => clearInterval(id);
  }, [running]);

  function reset() {
    snakeRef.current = initSnake; dirRef.current = {x:1,y:0};
    const f = {x:15,y:10}; foodRef.current = f; deadRef.current = false;
    setSnake(initSnake); setDir({x:1,y:0}); setFood(f);
    setDead(false); setScore(0); setRunning(false);
  }

  const size = GRID * CELL;
  return (
    <div style={{textAlign:"center"}}>
      <div style={{display:"flex",gap:16,justifyContent:"center",marginBottom:12}}>
        <div style={{background:"var(--primary-light)",borderRadius:"var(--radius-md)",padding:"8px 18px"}}>
          <div style={{fontSize:"1.3rem",fontWeight:800,color:"var(--primary-color)"}}>{score}</div>
          <div style={{fontSize:"0.7rem",color:"var(--text-muted)",fontWeight:700,textTransform:"uppercase"}}>Score</div>
        </div>
        <div style={{background:"var(--bg-secondary)",borderRadius:"var(--radius-md)",padding:"8px 18px"}}>
          <div style={{fontSize:"1.3rem",fontWeight:800,color:"var(--text-color)"}}>{best}</div>
          <div style={{fontSize:"0.7rem",color:"var(--text-muted)",fontWeight:700,textTransform:"uppercase"}}>Best</div>
        </div>
      </div>
      <div style={{position:"relative",display:"inline-block",border:"2px solid var(--border-color)",borderRadius:"var(--radius-md)",overflow:"hidden",background:"var(--bg-secondary)"}}>
        <svg width={size} height={size}>
          {Array.from({length:GRID+1}).map((_,i)=>(
            <g key={i}>
              <line x1={i*CELL} y1={0} x2={i*CELL} y2={size} stroke="var(--border-subtle)" strokeWidth="0.5"/>
              <line x1={0} y1={i*CELL} x2={size} y2={i*CELL} stroke="var(--border-subtle)" strokeWidth="0.5"/>
            </g>
          ))}
          <circle cx={food.x*CELL+CELL/2} cy={food.y*CELL+CELL/2} r={CELL/2-2} fill="var(--primary-color)" opacity="0.85"/>
          <text x={food.x*CELL+CELL/2} y={food.y*CELL+CELL/2+5} textAnchor="middle" fontSize="11">🍎</text>
          {snake.map((seg,i)=>(
            <rect key={i} x={seg.x*CELL+1} y={seg.y*CELL+1} width={CELL-2} height={CELL-2}
              rx="3" fill={i===0?"var(--primary-color)":"var(--secondary-color)"} opacity={i===0?1:0.75}/>
          ))}
        </svg>
        {!running && !dead && (
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.4)",borderRadius:"var(--radius-md)"}}>
            <div style={{color:"white",textAlign:"center"}}>
              <div style={{fontSize:"1.3rem",fontWeight:700,marginBottom:8}}>🐍 Snake</div>
              <button className="btn primary-btn" onClick={()=>setRunning(true)}>▶ Start</button>
              <p style={{fontSize:"0.75rem",marginTop:8,opacity:0.8}}>Arrow keys or WASD</p>
            </div>
          </div>
        )}
        {dead && (
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.5)",borderRadius:"var(--radius-md)"}}>
            <div style={{color:"white",textAlign:"center"}}>
              <div style={{fontSize:"1.5rem",marginBottom:4}}>💀 Game Over</div>
              <div style={{fontSize:"1.1rem",marginBottom:12}}>Score: {score}</div>
              <button className="btn primary-btn" onClick={reset}>Try Again</button>
            </div>
          </div>
        )}
      </div>
      {running && (
        <div style={{marginTop:12,display:"flex",justifyContent:"center"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,44px)",gridTemplateRows:"repeat(3,44px)",gap:4}}>
            <div/>
            <button onClick={()=>{dirRef.current={x:0,y:-1};setDir({x:0,y:-1});}} style={{borderRadius:"var(--radius-sm)",border:"1.5px solid var(--border-color)",background:"var(--bg-secondary)",cursor:"pointer",fontWeight:700,fontSize:"1rem"}}>↑</button>
            <div/>
            <button onClick={()=>{dirRef.current={x:-1,y:0};setDir({x:-1,y:0});}} style={{borderRadius:"var(--radius-sm)",border:"1.5px solid var(--border-color)",background:"var(--bg-secondary)",cursor:"pointer",fontWeight:700,fontSize:"1rem"}}>←</button>
            <button onClick={()=>setRunning(r=>!r)} style={{borderRadius:"var(--radius-sm)",border:"1.5px solid var(--border-color)",background:"var(--primary-light)",cursor:"pointer",fontSize:"0.75rem",fontWeight:700,color:"var(--primary-color)"}}>⏸</button>
            <button onClick={()=>{dirRef.current={x:1,y:0};setDir({x:1,y:0});}} style={{borderRadius:"var(--radius-sm)",border:"1.5px solid var(--border-color)",background:"var(--bg-secondary)",cursor:"pointer",fontWeight:700,fontSize:"1rem"}}>→</button>
            <div/>
            <button onClick={()=>{dirRef.current={x:0,y:1};setDir({x:0,y:1});}} style={{borderRadius:"var(--radius-sm)",border:"1.5px solid var(--border-color)",background:"var(--bg-secondary)",cursor:"pointer",fontWeight:700,fontSize:"1rem"}}>↓</button>
            <div/>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reaction Time Test ────────────────────────────────────────────────────────
function ReactionTest() {
  const [phase, setPhase]           = useState("idle");
  const [reactionTime, setReactionTime] = useState(null);
  const [times, setTimes]           = useState([]);
  const [startTime, setStartTime]   = useState(null);
  const timerRef                    = useRef(null);

  function startTest() {
    setPhase("waiting"); setReactionTime(null);
    const delay = 2000 + Math.random() * 4000;
    timerRef.current = setTimeout(() => { setPhase("ready"); setStartTime(performance.now()); }, delay);
  }

  function handleClick() {
    if (phase === "idle" || phase === "result") { startTest(); }
    else if (phase === "waiting") { clearTimeout(timerRef.current); setPhase("idle"); }
    else if (phase === "ready") {
      const rt = Math.round(performance.now() - startTime);
      setReactionTime(rt); setTimes(t => [...t, rt].slice(-10)); setPhase("result");
    }
  }

  const avg = times.length ? Math.round(times.reduce((a,b)=>a+b,0)/times.length) : null;
  const best = times.length ? Math.min(...times) : null;

  function getRating(ms) {
    if (ms < 200) return {label:"⚡ Superhuman!", color:"var(--secondary-color)"};
    if (ms < 250) return {label:"🎯 Excellent!", color:"var(--primary-color)"};
    if (ms < 350) return {label:"👍 Good", color:"var(--accent-color)"};
    if (ms < 500) return {label:"😊 Average", color:"var(--text-muted)"};
    return {label:"🐢 Slow — try again!", color:"var(--text-secondary-color)"};
  }

  const bgColor = phase === "ready" ? "var(--secondary-color)" : phase === "waiting" ? "var(--accent-color)" : "var(--primary-color)";
  const textMsg = phase === "idle" ? "Click to Start" : phase === "waiting" ? "Wait for green…" : phase === "ready" ? "CLICK NOW! 🟢" : `${reactionTime}ms`;

  return (
    <div style={{textAlign:"center"}}>
      <p style={{color:"var(--text-muted)",fontSize:"0.875rem",marginBottom:16}}>Click as fast as you can when the button turns green! 🟢</p>
      <div onClick={handleClick} style={{width:"100%",maxWidth:400,height:160,margin:"0 auto 20px",borderRadius:"var(--radius-xl)",background:bgColor,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",transition:"background 0.2s ease",userSelect:"none",boxShadow:"0 4px 20px rgba(0,0,0,0.15)"}}>
        <div style={{color:"white",fontWeight:800,fontSize:"clamp(1.2rem,3vw,1.8rem)",fontFamily:"'DM Serif Display', serif"}}>{textMsg}</div>
      </div>
      {phase === "result" && reactionTime && (
        <div style={{marginBottom:16}}>
          <p style={{fontFamily:"'DM Serif Display', serif",fontSize:"2rem",color:"var(--primary-color)",margin:"0 0 4px"}}>{reactionTime}ms</p>
          <p style={{fontWeight:700,color:getRating(reactionTime).color,marginBottom:12}}>{getRating(reactionTime).label}</p>
          <button className="btn primary-btn" onClick={startTest}>Try Again</button>
        </div>
      )}
      {times.length > 0 && (
        <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginTop:12}}>
          <div style={{background:"var(--primary-light)",borderRadius:"var(--radius-md)",padding:"10px 18px"}}>
            <div style={{fontWeight:800,color:"var(--primary-color)",fontSize:"1.2rem"}}>{best}ms</div>
            <div style={{fontSize:"0.7rem",color:"var(--text-muted)",fontWeight:700}}>BEST</div>
          </div>
          <div style={{background:"var(--bg-secondary)",borderRadius:"var(--radius-md)",padding:"10px 18px"}}>
            <div style={{fontWeight:800,color:"var(--text-color)",fontSize:"1.2rem"}}>{avg}ms</div>
            <div style={{fontSize:"0.7rem",color:"var(--text-muted)",fontWeight:700}}>AVERAGE</div>
          </div>
          <div style={{background:"var(--secondary-light)",borderRadius:"var(--radius-md)",padding:"10px 18px"}}>
            <div style={{fontWeight:800,color:"var(--secondary-color)",fontSize:"1.2rem"}}>{times.length}</div>
            <div style={{fontSize:"0.7rem",color:"var(--text-muted)",fontWeight:700}}>ATTEMPTS</div>
          </div>
        </div>
      )}
      {times.length > 1 && (
        <div style={{marginTop:16,display:"flex",gap:4,alignItems:"flex-end",justifyContent:"center",height:50}}>
          {times.map((t,i)=>{
            const maxT = Math.max(...times);
            const h = (t/maxT)*44;
            return <div key={i} title={`${t}ms`} style={{width:20,height:h,borderRadius:"3px 3px 0 0",background:t===best?"var(--secondary-color)":"var(--primary-color)",opacity:0.7+0.3*(i/times.length),transition:"all 0.3s"}}/>;
          })}
        </div>
      )}
    </div>
  );
}

// ─── Math Sprint — 40 Levels ──────────────────────────────────────────────────
const MATH_LEVELS = {
  1:[{q:"12 + 17 = ?",a:"29"},{q:"34 + 25 = ?",a:"59"},{q:"51 − 18 = ?",a:"33"},{q:"55 + 46 = ?",a:"101"},{q:"91 − 44 = ?",a:"47"}],
  2:[{q:"7 × 8 = ?",a:"56"},{q:"9 × 6 = ?",a:"54"},{q:"12 × 7 = ?",a:"84"},{q:"8 × 15 = ?",a:"120"},{q:"180 ÷ 15 = ?",a:"12"}],
  3:[{q:"3 + 4 × 5 = ?",a:"23",hint:"Multiplication before addition"},{q:"(6 + 4) × 3 = ?",a:"30"},{q:"20 − 3 × 4 = ?",a:"8"},{q:"6² ÷ (2 × 3) = ?",a:"6"}],
  4:[{q:"1/2 + 1/3 = ? (as fraction)",a:"5/6"},{q:"3/4 − 1/4 = ?",a:"1/2"},{q:"0.125 × 8 = ?",a:"1"},{q:"5/8 + 3/8 = ?",a:"1"}],
  5:[{q:"20% of 150 = ?",a:"30"},{q:"35% of 200 = ?",a:"70"},{q:"What % of 200 is 50?",a:"25"}],
  6:[{q:"x + 7 = 15. x = ?",a:"8"},{q:"2x = 18. x = ?",a:"9"},{q:"9x = 72. x = ?",a:"8"},{q:"2x/3 = 8. x = ?",a:"12"}],
  7:[{q:"If a=3, b=4: a² + b² = ?",a:"25"},{q:"Factorise: x² − 5x + 6 = ?",a:"(x−2)(x−3)"}],
  8:[{q:"x+y=10, x−y=4. x=?",a:"7"},{q:"5x−2y=16, 3x−2y=8. x=?",a:"4"}],
  9:[{q:"Solve x²−5x+6=0. Smaller root=?",a:"2"},{q:"Solve x²−x−2=0. Larger root=?",a:"2"}],
  10:[{q:"Solve 2x + 3 > 11. x > ?",a:"4"},{q:"Smallest prime > 20?",a:"23"}],
  11:[{q:"Sum of interior angles of a triangle = ?°",a:"180"},{q:"A right angle = ?°",a:"90"},{q:"Interior angle sum of hexagon = ?°",a:"720"}],
  12:[{q:"Area of rectangle 8×5 = ?",a:"40"},{q:"Area of square side 11 = ?",a:"121"}],
  13:[{q:"Right △ legs 3,4. Hypotenuse = ?",a:"5"},{q:"Equation of line slope 2, y-int 3: y=?",a:"y=2x+3"}],
  14:[{q:"sin 30° = ?",a:"0.5"},{q:"cos 60° = ?",a:"0.5"},{q:"1 + tan²45° = ?",a:"2"},{q:"sec 60° = ? (1/cos60°)",a:"2"}],
  15:[{q:"AP: 3,7,11,... 10th term=?",a:"39"},{q:"Sum of first 5 terms GP a=1 r=2?",a:"31"}],
  16:[{q:"f(x)=2x+3. f(5)=?",a:"13"},{q:"f(x)=(x+2)(x−3). zeros at x=?",a:"−2,3"}],
  17:[{q:"log₂(8) = ?",a:"3"},{q:"log₁₀(1000) = ?",a:"3"},{q:"4^x = 256. x = ?",a:"4"},{q:"log₃(3^5) = ?",a:"5"}],
  18:[{q:"lim x→2 (x²) = ?",a:"4"},{q:"lim x→0 (e^x−1)/x = ?",a:"1"}],
  19:[{q:"d/dx (x³) = ?",a:"3x²"},{q:"d/dx (x⁵) = ?",a:"5x⁴"},{q:"f(x)=x²−4x. f'(3)=?",a:"2"}],
  20:[{q:"∫ x dx = ?",a:"x²/2 + C"},{q:"∫ x² dx = ?",a:"x³/3 + C"},{q:"∫₁³ 3 dx = ?",a:"6"}],
  21:[{q:"Volume of cube side 5 = ?",a:"125"},{q:"Lateral surface area of cube side 6 = ?",a:"144"}],
  22:[{q:"5! = ?",a:"120"},{q:"6! = ?",a:"720"},{q:"P(no heads in 3 tosses) = ?",a:"1/8"}],
  23:[{q:"Mean of 4,7,9,10,5 = ?",a:"7"},{q:"Mode of 5,5,5,5 = ?",a:"5"}],
  24:[{q:"A={1,2,3} B={2,3,4}. A∪B = ?",a:"{1,2,3,4}"},{q:"A∩∅ = ?",a:"∅"}],
  25:[{q:"Remainder when x³−2x+1 divided by (x−1) = ?",a:"0"},{q:"If α,β roots of x²−7x+10=0: α³+β³ = ?",a:"133"}],
  26:[{q:"det [[2,3],[1,4]] = ?",a:"5"},{q:"det [[5,0],[0,5]] = ?",a:"25"},{q:"Cofactor of element (1,1) of [[2,3],[1,4]] = ?",a:"4"}],
  27:[{q:"i² = ?",a:"−1"},{q:"i³ = ?",a:"−i"},{q:"Polar form: r(cosθ+i sinθ). For 1+i, r=?",a:"√2"}],
  28:[{q:"Magnitude of vector (3,4) = ?",a:"5"},{q:"Dot product (1,2)·(3,4) = ?",a:"11"},{q:"Magnitude of (6,8) = ?",a:"10"}],
  29:[{q:"d/dx sin(3x) = ?",a:"3cos(3x)"},{q:"d/dx (x² ln x) = ?",a:"x(2ln x+1)"}],
  30:[{q:"∫ sin(2x) dx = ?",a:"−cos(2x)/2 + C"},{q:"∫₁ᵉ 1/x dx = ?",a:"1"}],
  31:[{q:"CP=200, SP=250. Profit % = ?",a:"25"},{q:"CP=500, SP=400. Loss % = ?",a:"20"},{q:"Trade discount 25% on 800. Invoice price = ?",a:"600"}],
  32:[{q:"A does work in 10 days, B in 15. Together in ? days",a:"6"},{q:"A alone 18, B alone 9. Days to finish together=?",a:"6"}],
  33:[{q:"Distance 240km, time 4h. Speed = ?",a:"60 km/h"},{q:"Speed 60, time 2.5h. Distance = ?",a:"150 km"},{q:"If speed halved, time for same distance?",a:"doubled"}],
  34:[{q:"SI: P=1000 R=5% T=3yr. SI=?",a:"150"},{q:"Time to triple at 20% SI = ?",a:"10 yr"}],
  35:[{q:"A number is 4 more than 3× its digits sum. 2-digit, digit sum 7, tens+2=units. Number=?",a:"25"},{q:"If |3x−7|=2, values of x = ?",a:"3 or 5/3"}],
  36:[{q:"E[X]: X=1,2,3 with P=1/3 each. E[X]=?",a:"2"},{q:"Median of geometric distribution larger than mean?",a:"Yes"}],
  37:[{q:"Minimax strategy: Player minimises maximum loss. True or False?",a:"True"},{q:"Decision tree: chance node represented by?",a:"circle"}],
  38:[{q:"Volume of revolution of y=√x from 0 to 4 about x-axis = π∫₀⁴x dx = ?",a:"8π"},{q:"L'Hôpital: lim x→0 (sin x/x) = ?",a:"1"}],
  39:[{q:"Euler's totient φ(10) = ?",a:"4"},{q:"φ(p) for prime p = ?",a:"p−1"},{q:"Sum of all residues mod 5: 0+1+2+3+4=?",a:"10"}],
  40:[{q:"If x+1/x=3, x²+1/x²=?",a:"7"},{q:"If f(x+y)=f(x)+f(y) and f(1)=3, f(7)=?",a:"21"}],
};

function getLevelConfig(lvl) {
  let timer;
  if      (lvl <= 5)  timer = 30;
  else if (lvl <= 15) timer = 25;
  else if (lvl <= 30) timer = 22;
  else                timer = 120;
  const labels = {
    1:"Simple Addition & Subtraction",2:"Multiplication & Division",3:"Order of Operations",4:"Fractions & Decimals",5:"Percentages & Ratios",
    6:"Linear Equations",7:"Algebraic Expressions",8:"Simultaneous Equations",9:"Quadratic Equations",10:"Inequalities & Number Theory",
    11:"Geometry — Angles & Triangles",12:"Area & Perimeter",13:"Pythagoras & Coordinates",14:"Trigonometry Basics",15:"Progressions & Series",
    16:"Functions & Graphs",17:"Logarithms & Exponentials",18:"Limits",19:"Derivatives",20:"Integrals",
    21:"3D Geometry & Volume",22:"Combinatorics & Probability",23:"Statistics & Data",24:"Set Theory & Logic",25:"Advanced Algebra & Polynomials",
    26:"Matrices & Determinants",27:"Complex Numbers",28:"Vectors & Mechanics",29:"Chain Rule & Calculus Applications",30:"Integration Techniques",
    31:"⏱ Profit & Loss (2 min)",32:"⏱ Time & Work (2 min)",33:"⏱ Speed, Distance & Time (2 min)",34:"⏱ Mixtures & Interest (2 min)",
    35:"⏱ Number Puzzles (2 min)",36:"⏱ Probability & Statistics (2 min)",37:"⏱ Decision Maths (2 min)",38:"⏱ Advanced Calculus (2 min)",
    39:"⏱ Number Theory & Algebra (2 min)",40:"⏱ Mixed Olympiad Style (2 min)",
  };
  return { label: labels[lvl] || `Level ${lvl}`, timer };
}

function MathSprint() {
  const MAX_LEVEL    = 40;
  const QS_PER_LEVEL = 5;

  function drawQuestions(lvl) {
    const pool = [...(MATH_LEVELS[lvl] || MATH_LEVELS[1])];
    return pool.sort(() => Math.random() - 0.5).slice(0, QS_PER_LEVEL);
  }
  function getTimer(lvl) { return getLevelConfig(lvl).timer; }
  function normalize(s) { return String(s).toLowerCase().replace(/\s+/g,"").replace(/[−–]/g,"-"); }

  const [level, setLevel]             = useState(1);
  const [levelPool, setLevelPool]     = useState([]);
  const [questionNum, setQuestionNum] = useState(0);
  const [input, setInput]             = useState("");
  const [totalScore, setTotalScore]   = useState(0);
  const [levelScore, setLevelScore]   = useState(0);
  const [lives, setLives]             = useState(3);
  const [timeLeft, setTimeLeft]       = useState(null);
  const [running, setRunning]         = useState(false);
  const [gameState, setGameState]     = useState("menu");
  const [streak, setStreak]           = useState(0);
  const [feedback, setFeedback]       = useState(null);
  const [history, setHistory]         = useState([]);
  const [showHint, setShowHint]       = useState(false);
  const inputRef  = useRef(null);
  const timerRef  = useRef(null);
  const livesRef  = useRef(3);

  const currentQ = levelPool[questionNum] || null;

  function startLevel(lvl) {
    const pool = drawQuestions(lvl);
    setLevel(lvl); setLevelPool(pool); setQuestionNum(0); setLevelScore(0);
    setLives(3); livesRef.current = 3; setStreak(0); setInput(""); setFeedback(null);
    setShowHint(false); setTimeLeft(getTimer(lvl)); setRunning(true); setGameState("playing");
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  useEffect(() => {
    if (!running || gameState !== "playing") { clearInterval(timerRef.current); return; }
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); handleTimeUp(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running, questionNum, gameState]);

  function handleTimeUp() {
    const correctAns = currentQ?.a || "—";
    setFeedback({ type:"timeout", msg:`⏱ Time up! Answer: ${correctAns}` });
    setShowHint(false);
    const newLives = livesRef.current - 1;
    livesRef.current = newLives; setLives(newLives); setStreak(0);
    if (newLives <= 0) { setRunning(false); setGameState("gameover"); }
    else { setTimeout(() => advanceQuestion(), 1200); }
  }

  function advanceQuestion() {
    const next = questionNum + 1;
    clearInterval(timerRef.current);
    if (next >= QS_PER_LEVEL) { setRunning(false); setFeedback(null); setGameState("levelDone"); }
    else {
      setQuestionNum(next); setInput(""); setFeedback(null);
      setShowHint(false); setTimeLeft(getTimer(level));
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  function checkAnswer() {
    if (!currentQ || !running) return;
    const raw = input.trim();
    if (!raw) return;
    clearInterval(timerRef.current);
    const isCorrect = normalize(raw) === normalize(currentQ.a);
    if (isCorrect) {
      const timeBonus   = Math.max(0, Math.floor(timeLeft / 5));
      const streakBonus = streak >= 4 ? 3 : streak >= 2 ? 2 : 1;
      const pts         = (10 + timeBonus) * streakBonus * Math.ceil(level / 5);
      setTotalScore(s => s + pts); setLevelScore(s => s + pts); setStreak(s => s + 1);
      setFeedback({ type:"correct", msg: streak >= 3 ? `🔥 +${pts}pts (${streak+1}x streak!)` : `✅ +${pts}pts` });
      setTimeout(() => advanceQuestion(), 600);
    } else {
      const newLives = livesRef.current - 1;
      livesRef.current = newLives; setLives(newLives); setStreak(0);
      setFeedback({ type:"wrong", msg:`❌ Correct: ${currentQ.a}` }); setInput("");
      if (newLives <= 0) { setRunning(false); setGameState("gameover"); }
      else { setTimeout(() => advanceQuestion(), 900); }
    }
  }

  const cfg      = getLevelConfig(level);
  const maxTimer = getTimer(level);
  const isQuant  = level >= 31;

  function levelBand(l) {
    if (l <= 5)  return { bg:"var(--secondary-light)", c:"var(--secondary-color)", label:"Arithmetic" };
    if (l <= 10) return { bg:"var(--accent-light)",    c:"var(--accent-color)",    label:"Algebra I" };
    if (l <= 15) return { bg:"var(--primary-light)",   c:"var(--primary-color)",   label:"Algebra II" };
    if (l <= 20) return { bg:"#e0f0ff",                c:"#2563eb",                label:"Geometry" };
    if (l <= 25) return { bg:"#f3e8ff",                c:"#7c3aed",                label:"Adv Algebra" };
    if (l <= 30) return { bg:"#fef3c7",                c:"#d97706",                label:"Calculus" };
    return               { bg:"#fee2e2",               c:"#dc2626",                label:"Quant" };
  }

  if (gameState === "menu") {
    return (
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:6}}>🧮</div>
        <p style={{fontFamily:"'DM Serif Display', serif",fontSize:"1.35rem",marginBottom:4}}>Math Sprint</p>
        <p style={{color:"var(--text-muted)",marginBottom:20,fontSize:"0.875rem"}}>40 levels · 5 questions each · 3 lives per level</p>
        <div style={{display:"flex",gap:6,justifyContent:"center",flexWrap:"wrap",marginBottom:16}}>
          {[{r:"1-5",l:"Arithmetic"},{r:"6-15",l:"Algebra"},{r:"16-25",l:"Geometry"},{r:"26-30",l:"Calculus"},{r:"31-40",l:"Quant ⏱2min"}].map(b=>(
            <span key={b.r} style={{fontSize:"0.65rem",fontWeight:700,background:"var(--bg-secondary)",border:"1px solid var(--border-color)",borderRadius:"var(--radius-full)",padding:"3px 10px",color:"var(--text-muted)"}}>{b.r}: {b.l}</span>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(8,1fr)",gap:5,maxWidth:500,margin:"0 auto 20px"}}>
          {Array.from({length:MAX_LEVEL}).map((_,i)=>{
            const l=i+1; const done=history.some(h=>h.level===l); const band=levelBand(l);
            return (
              <button key={l} onClick={()=>startLevel(l)} title={getLevelConfig(l).label} style={{
                padding:"7px 0",borderRadius:"var(--radius-sm)",fontWeight:700,fontSize:"0.78rem",
                border:`1.5px solid ${done?band.c:"var(--border-color)"}`,cursor:"pointer",
                background:done?band.bg:"var(--bg-secondary)",color:done?band.c:"var(--text-muted)",
                position:"relative",transition:"all 0.15s",
              }}>
                {l}{done && <span style={{position:"absolute",top:-3,right:-3,fontSize:"0.5rem",lineHeight:1}}>✓</span>}
              </button>
            );
          })}
        </div>
        {totalScore > 0 && <p style={{color:"var(--primary-color)",fontWeight:700,marginBottom:12,fontSize:"0.9rem"}}>🏆 Total score: {totalScore}</p>}
        <div style={{display:"flex",gap:10,justifyContent:"center"}}>
          <button className="btn primary-btn" onClick={()=>{setTotalScore(0);setHistory([]);startLevel(1);}}>Start from Level 1</button>
          {history.length > 0 && <button className="btn secondary-btn" onClick={()=>startLevel(Math.min(history.length+1,MAX_LEVEL))}>Resume L{Math.min(history.length+1,MAX_LEVEL)}</button>}
        </div>
      </div>
    );
  }
  if (gameState === "gameover") {
    return (
      <div style={{textAlign:"center",padding:"20px 0"}}>
        <div style={{fontSize:52,marginBottom:8}}>💀</div>
        <p style={{fontFamily:"'DM Serif Display', serif",fontSize:"1.7rem",color:"var(--primary-color)",margin:"0 0 6px"}}>Game Over!</p>
        <p style={{color:"var(--text-muted)",marginBottom:4}}>Stopped at Level {level} — {getLevelConfig(level).label}</p>
        <p style={{fontWeight:700,fontSize:"1.2rem",marginBottom:20}}>Total Score: <span style={{color:"var(--primary-color)"}}>{totalScore}</span></p>
        <div style={{display:"flex",gap:10,justifyContent:"center"}}>
          <button className="btn primary-btn" onClick={()=>startLevel(level)}>Retry Level {level}</button>
          <button className="btn secondary-btn" onClick={()=>setGameState("menu")}>Level Select</button>
        </div>
      </div>
    );
  }
  if (gameState === "victory") {
    return (
      <div style={{textAlign:"center",padding:"20px 0"}}>
        <div style={{fontSize:52,marginBottom:8}}>🏆</div>
        <p style={{fontFamily:"'DM Serif Display', serif",fontSize:"1.7rem",color:"var(--primary-color)",margin:"0 0 8px"}}>Math Legend! 🔥</p>
        <p style={{color:"var(--text-muted)",marginBottom:4}}>You conquered all 40 levels!</p>
        <p style={{fontWeight:700,fontSize:"1.4rem",marginBottom:20}}>Final Score: <span style={{color:"var(--primary-color)"}}>{totalScore}</span></p>
        <button className="btn primary-btn" onClick={()=>{setTotalScore(0);setHistory([]);setGameState("menu");}}>Play Again</button>
      </div>
    );
  }
  if (gameState === "levelDone") {
    const nextLvl = level + 1;
    const nextCfg = nextLvl <= MAX_LEVEL ? getLevelConfig(nextLvl) : null;
    return (
      <div style={{textAlign:"center",padding:"16px 0"}}>
        <div style={{fontSize:48,marginBottom:6}}>⭐</div>
        <p style={{fontFamily:"'DM Serif Display', serif",fontSize:"1.6rem",color:"var(--primary-color)",margin:"0 0 4px"}}>Level {level} Complete!</p>
        <p style={{color:"var(--text-muted)",marginBottom:4,fontSize:"0.875rem"}}>{getLevelConfig(level).label}</p>
        <p style={{fontWeight:700,fontSize:"1.1rem",marginBottom:20}}>+{levelScore} pts &nbsp;·&nbsp; Total: <span style={{color:"var(--primary-color)"}}>{totalScore}</span></p>
        {nextCfg ? (
          <div>
            <div style={{background:"var(--bg-secondary)",borderRadius:"var(--radius-md)",padding:"12px 20px",display:"inline-block",marginBottom:16,textAlign:"left"}}>
              <div style={{fontSize:"0.65rem",color:"var(--text-muted)",fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Next — Level {nextLvl}</div>
              <div style={{fontWeight:700,color:"var(--text-color)",marginBottom:4}}>{nextCfg.label}</div>
              <div style={{fontSize:"0.75rem",color:"var(--primary-color)"}}>⏱ {nextCfg.timer >= 60 ? `${nextCfg.timer/60} min` : `${nextCfg.timer}s`} per question</div>
            </div>
            <br/>
            <button className="btn primary-btn" onClick={()=>{setHistory(h=>[...h,{level,score:levelScore}]);startLevel(nextLvl);}}>Level {nextLvl} →</button>
          </div>
        ) : (
          <button className="btn primary-btn" onClick={()=>setGameState("victory")}>🏆 See Final Results</button>
        )}
        <br/>
        <button className="btn secondary-btn" style={{marginTop:12}} onClick={()=>setGameState("menu")}>Level Select</button>
      </div>
    );
  }
  const band = levelBand(level);
  const urgentTime = isQuant ? 30 : Math.min(8, Math.floor(maxTimer * 0.25));
  const warnTime   = isQuant ? 60 : Math.min(15, Math.floor(maxTimer * 0.5));
  return (
    <div style={{textAlign:"center"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{textAlign:"left"}}>
          <div style={{fontSize:"0.65rem",color:"var(--text-muted)",fontWeight:700,textTransform:"uppercase"}}>Level</div>
          <div style={{fontWeight:800,color:band.c,fontSize:"1.1rem"}}>{level}<span style={{fontSize:"0.6rem",color:"var(--text-muted)",marginLeft:3}}>/{MAX_LEVEL}</span></div>
        </div>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:"0.65rem",color:"var(--text-muted)",fontWeight:700,textTransform:"uppercase"}}>Lives</div>
          <div style={{fontSize:"1rem"}}>{Array.from({length:3}).map((_,i)=><span key={i}>{i<lives?"❤️":"🖤"}</span>)}</div>
        </div>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:"0.65rem",color:"var(--text-muted)",fontWeight:700,textTransform:"uppercase"}}>Q</div>
          <div style={{fontWeight:700,color:"var(--text-color)",fontSize:"0.9rem"}}>{questionNum+1}/{QS_PER_LEVEL}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:"0.65rem",color:"var(--text-muted)",fontWeight:700,textTransform:"uppercase"}}>Score</div>
          <div style={{fontWeight:700,color:band.c,fontSize:"0.9rem"}}>{totalScore}</div>
        </div>
      </div>
      <div style={{display:"inline-block",background:band.bg,color:band.c,fontWeight:700,fontSize:"0.7rem",padding:"3px 12px",borderRadius:"var(--radius-full)",marginBottom:10}}>{band.label} · {cfg.label}</div>
      <div style={{display:"flex",gap:5,justifyContent:"center",marginBottom:12}}>
        {Array.from({length:QS_PER_LEVEL}).map((_,i)=>(<div key={i} style={{width:9,height:9,borderRadius:"50%",background:i<questionNum?"var(--secondary-color)":i===questionNum?"var(--primary-color)":"var(--border-color)",border:i===questionNum?"2px solid var(--primary-color)":"2px solid transparent",transition:"all 0.3s"}}/>))}
      </div>
      <div style={{height:7,background:"var(--border-color)",borderRadius:4,marginBottom:14,overflow:"hidden"}}>
        <div style={{height:"100%",borderRadius:4,transition:"width 1s linear, background 0.3s",width:`${(timeLeft/maxTimer)*100}%`,background:timeLeft<=urgentTime?"#e53e3e":timeLeft<=warnTime?"var(--accent-color)":"var(--secondary-color)"}}/>
      </div>
      <div style={{fontFamily:"'DM Serif Display', serif",fontSize:isQuant?"clamp(1.8rem,3vw,2.4rem)":"clamp(2.2rem,4vw,3rem)",fontWeight:800,lineHeight:1,marginBottom:2,transition:"color 0.3s",color:timeLeft<=urgentTime?"#e53e3e":timeLeft<=warnTime?"var(--accent-color)":"var(--text-color)"}}>
        {isQuant ? `${Math.floor(timeLeft/60)}:${String(timeLeft%60).padStart(2,"0")}` : timeLeft}
      </div>
      <div style={{fontSize:"0.65rem",color:"var(--text-muted)",marginBottom:14}}>{isQuant ? "minutes remaining" : "seconds"}</div>
      <div style={{background:"var(--bg-secondary)",borderRadius:"var(--radius-md)",padding:"16px 20px",marginBottom:12,minHeight:isQuant?72:56,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Serif Display', serif",fontSize:isQuant?"clamp(1rem,2.2vw,1.3rem)":"clamp(1.4rem,3.5vw,2rem)",color:"var(--text-color)",lineHeight:1.4,textAlign:"center"}}>{currentQ?.q || ""}</div>
      {currentQ?.hint && !showHint && (<button onClick={()=>setShowHint(true)} style={{background:"none",border:"none",fontSize:"0.75rem",color:"var(--primary-color)",cursor:"pointer",textDecoration:"underline",marginBottom:6}}>Show hint</button>)}
      {showHint && currentQ?.hint && (<div style={{fontSize:"0.8rem",color:"var(--accent-color)",marginBottom:8,fontWeight:600}}>💡 {currentQ.hint}</div>)}
      <div style={{minHeight:24,marginBottom:8,fontSize:"0.9rem",fontWeight:700,color:feedback?.type==="correct"?"var(--secondary-color)":feedback?.type==="timeout"?"var(--accent-color)":"var(--primary-color)"}}>{feedback?.msg || (streak>1?`🔥 ${streak}x streak!`:"")}</div>
      <div style={{display:"flex",gap:8,justifyContent:"center"}}>
        <input ref={inputRef} type="text" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&checkAnswer()} placeholder={isQuant?"Type your answer…":"Answer…"} style={{width:isQuant?220:160,textAlign:"center",fontSize:isQuant?"1rem":"1.3rem",fontWeight:700}}/>
        <button className="btn primary-btn" onClick={checkAnswer} style={{fontSize:"1rem",padding:"10px 18px"}}>→</button>
      </div>
      {isQuant && (<p style={{fontSize:"0.7rem",color:"var(--text-muted)",marginTop:8}}>Type exact answer (e.g. "25", "6 h", "1/6")</p>)}
      <button className="btn secondary-btn" style={{marginTop:14,fontSize:"0.72rem"}} onClick={()=>{clearInterval(timerRef.current);setGameState("menu");}}>← Menu</button>
    </div>
  );
}

// ─── 2-PLAYER TRIVIA DUEL — (unchanged, full code preserved) ──────────────────
const TRIVIA_QUESTIONS = [
  { q: "What planet is known as the Red Planet?", opts: ["Venus","Mars","Jupiter"], ans: 1, cat:"🔬 Science" },
  { q: "Which organ detoxifies body?", opts: ["Kidney","Liver","Heart"], ans: 1, cat:"🔬 Science" },
  { q: "Longest river in India?", opts: ["Ganga","Godavari","Brahmaputra"], ans: 0, cat:"🇮🇳 India GK" },
  { q: "Which state shares most borders?", opts: ["UP","MP","Rajasthan"], ans: 0, cat:"🇮🇳 India GK" },
  { q: "Indian constitution adopted in?", opts: ["1949","1950","1947"], ans: 0, cat:"🏛 Politics" },
  { q: "First amendment year?", opts: ["1951","1950","1955"], ans: 0, cat:"🏛 Politics" },
  { q: "Most ODI runs?", opts: ["Sachin","Kohli","Ponting"], ans: 0, cat:"🏏 Cricket" },
  { q: "Which trophy India vs Aus?", opts: ["Border-Gavaskar","Ashes","Pataudi"], ans: 0, cat:"🏏 Cricket" },
  { q: "Largest ocean?", opts: ["Pacific","Atlantic","Indian"], ans: 0, cat:"🌍 Geography" },
  { q: "Highest peak world?", opts: ["Everest","K2","Kangchen"], ans: 0, cat:"🌍 Geography" },
  { q: "Which pole colder?", opts: ["South","North","Same"], ans: 0, cat:"🌍 Geography" },
  { q: "Director of Inception?", opts: ["Nolan","Scott","Spielberg"], ans: 0, cat:"🎬 Movies" },
  { q: "Who discovered sea route India?", opts: ["Vasco da Gama","Columbus","Cook"], ans: 0, cat:"📜 History" },
  { q: "Who built Red Fort?", opts: ["Shah Jahan","Akbar","Babur"], ans: 0, cat:"📜 History" },
  { q: "Which empire Ashoka?", opts: ["Maurya","Gupta","Mughal"], ans: 0, cat:"📜 History" },
  { q: "Which movie won Oscar 2020?", opts: ["Parasite","Joker","1917"], ans: 0, cat:"🎬 Movies" },
  { q: "Which particle determines atomic number?", opts: ["Electron","Proton","Neutron"], ans: 1, cat:"🔬 Science" },
  { q: "Which scientist proposed laws of motion?", opts: ["Newton","Galileo","Kepler"], ans: 0, cat:"🔬 Science" },
  { q: "Which is India's longest dam?", opts: ["Hirakud","Bhakra","Tehri"], ans: 0, cat:"🇮🇳 India GK" },
  { q: "Which Indian city called City of Lakes?", opts: ["Udaipur","Bhopal","Nainital"], ans: 0, cat:"🇮🇳 India GK" },
  { q: "Which article gives Right to Constitutional Remedies?", opts: ["32","19","21"], ans: 0, cat:"🏛 Politics" },
  { q: "Which article defines citizenship?", opts: ["5","10","15"], ans: 0, cat:"🏛 Politics" },
  { q: "Who has most Test centuries?", opts: ["Sachin","Smith","Kohli"], ans: 0, cat:"🏏 Cricket" },
  { q: "Who has most international wickets?", opts: ["Muralitharan","Warne","Wasim"], ans: 0, cat:"🏏 Cricket" },
  { q: "Which strait separates Asia and Europe?", opts: ["Bosporus","Hormuz","Malacca"], ans: 0, cat:"🌍 Geography" },
  { q: "Which ocean smallest?", opts: ["Arctic","Indian","Southern"], ans: 0, cat:"🌍 Geography" },
  { q: "Who directed Oppenheimer?", opts: ["Christopher Nolan","Spielberg","Villeneuve"], ans: 0, cat:"🎬 Movies" },
  { q: "Which novel written by Tolkien?", opts: ["Lord of the Rings","Harry Potter","Narnia"], ans: 0, cat:"📚 Books" },
];

function TwoPlayerDuel() {
  const ROUNDS = 10;
  const ROUND_TIME = 15;
  const [gameState, setGameState]       = useState("lobby");
  const [questions, setQuestions]       = useState([]);
  const [roundIdx, setRoundIdx]         = useState(0);
  const [scores, setScores]             = useState([0, 0]);
  const [timeLeft, setTimeLeft]         = useState(ROUND_TIME);
  const [answered, setAnswered]         = useState(null);
  const [countdown, setCountdown]       = useState(3);
  const [roundHistory, setRoundHistory] = useState([]);
  const timerRef    = useRef(null);
  const answeredRef = useRef(null);
  const [p1name, setP1name] = useState("Player 1");
  const [p2name, setP2name] = useState("Player 2");

  useEffect(() => {
    if (gameState !== "playing") return;
    const handleKey = e => {
      if (answeredRef.current) return;
      const p1map = { a:0, s:1, d:2 };
      const p2map = { ArrowLeft:0, ArrowDown:1, ArrowRight:2 };
      const q = questions[roundIdx];
      if (!q) return;
      if (e.key.toLowerCase() in p1map) { e.preventDefault(); buzzer(0, p1map[e.key.toLowerCase()], q); }
      else if (e.key in p2map) { e.preventDefault(); buzzer(1, p2map[e.key], q); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [gameState, roundIdx, questions]);

  function buzzer(player, choice, q) {
    if (answeredRef.current) return;
    clearInterval(timerRef.current);
    const correct = choice === q.ans;
    const result = { player, choice, correct };
    answeredRef.current = result; setAnswered(result);
    setScores(s => { const ns=[...s]; ns[player] += correct ? 2 : -1; return ns; });
    setRoundHistory(h => [...h, { q: q.q, winner: correct ? player : null, correct }]);
    setTimeout(() => nextRound(), 1800);
  }

  function handleTimeUp() {
    if (answeredRef.current) return;
    const result = { player: null, choice: null, correct: false };
    answeredRef.current = result; setAnswered(result);
    setRoundHistory(h => [...h, { q: questions[roundIdx]?.q, winner: null, correct: false }]);
    setTimeout(() => nextRound(), 1500);
  }

  function nextRound() {
    answeredRef.current = null;
    const next = roundIdx + 1;
    if (next >= ROUNDS) { setGameState("final"); }
    else { setRoundIdx(next); setAnswered(null); setTimeLeft(ROUND_TIME); }
  }

  useEffect(() => {
    if (gameState !== "playing") return;
    answeredRef.current = null; setAnswered(null); setTimeLeft(ROUND_TIME);
  }, [roundIdx, gameState]);

  useEffect(() => {
    if (gameState !== "playing" || answered) { clearInterval(timerRef.current); return; }
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => { if (t <= 1) { clearInterval(timerRef.current); handleTimeUp(); return 0; } return t - 1; });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [gameState, answered, roundIdx]);

  function startGame() {
    const shuffled = [...TRIVIA_QUESTIONS].sort(() => Math.random() - 0.5).slice(0, ROUNDS);
    setQuestions(shuffled); setRoundIdx(0); setScores([0, 0]); setRoundHistory([]);
    answeredRef.current = null; setAnswered(null); setCountdown(3); setGameState("countdown");
    let c = 3;
    const cid = setInterval(() => { c--; setCountdown(c); if (c <= 0) { clearInterval(cid); setGameState("playing"); } }, 800);
  }

  const q = questions[roundIdx];
  const names = [p1name, p2name];
  const colors = ["var(--primary-color)","var(--secondary-color)"];
  const optionKeys = [["A","S","D"],["←","↓","→"]];

  if (gameState === "lobby") {
    return (
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:8}}>⚔️</div>
        <p style={{fontFamily:"'DM Serif Display', serif",fontSize:"1.4rem",marginBottom:4}}>2-Player Trivia Duel</p>
        <p style={{color:"var(--text-muted)",marginBottom:24,maxWidth:460,margin:"0 auto 24px"}}>Same keyboard, two players. Buzz first with the correct answer to earn points. Wrong answer = −1 point!</p>
        <div style={{display:"flex",gap:16,justifyContent:"center",marginBottom:24,flexWrap:"wrap"}}>
          <div style={{background:"var(--primary-light)",borderRadius:"var(--radius-lg)",padding:"20px 24px",minWidth:200}}>
            <div style={{fontSize:"1.4rem",marginBottom:8}}>🟦 Player 1</div>
            <input value={p1name} onChange={e=>setP1name(e.target.value)} placeholder="Player 1 name" style={{textAlign:"center",fontWeight:700,width:"100%",marginBottom:12}}/>
            <div style={{fontSize:"0.75rem",color:"var(--primary-color)",fontWeight:700}}>Keys: A / S / D</div>
            <div style={{fontSize:"0.7rem",color:"var(--text-muted)",marginTop:4}}>A = option 1 &nbsp; S = option 2 &nbsp; D = option 3</div>
          </div>
          <div style={{background:"var(--secondary-light)",borderRadius:"var(--radius-lg)",padding:"20px 24px",minWidth:200}}>
            <div style={{fontSize:"1.4rem",marginBottom:8}}>🟥 Player 2</div>
            <input value={p2name} onChange={e=>setP2name(e.target.value)} placeholder="Player 2 name" style={{textAlign:"center",fontWeight:700,width:"100%",marginBottom:12}}/>
            <div style={{fontSize:"0.75rem",color:"var(--secondary-color)",fontWeight:700}}>Keys: ← / ↓ / →</div>
            <div style={{fontSize:"0.7rem",color:"var(--text-muted)",marginTop:4}}>← = option 1 &nbsp; ↓ = option 2 &nbsp; → = option 3</div>
          </div>
        </div>
        <div style={{background:"var(--bg-secondary)",borderRadius:"var(--radius-md)",padding:"12px 20px",display:"inline-block",marginBottom:20}}>
          <div style={{fontSize:"0.8rem",color:"var(--text-muted)"}}>🎯 {ROUNDS} rounds &nbsp;|&nbsp; ⏱ {ROUND_TIME}s each &nbsp;|&nbsp; ✅ Correct = +2 &nbsp;|&nbsp; ❌ Wrong = −1</div>
        </div>
        <br/>
        <button className="btn primary-btn" style={{fontSize:"1.1rem",padding:"12px 32px"}} onClick={startGame}>Start Duel! ⚔️</button>
      </div>
    );
  }
  if (gameState === "countdown") {
    return (
      <div style={{textAlign:"center",padding:"40px 0"}}>
        <div style={{fontFamily:"'DM Serif Display', serif",fontSize:"6rem",color:"var(--primary-color)",lineHeight:1,marginBottom:8}}>{countdown === 0 ? "GO!" : countdown}</div>
        <p style={{color:"var(--text-muted)"}}>Get ready…</p>
      </div>
    );
  }
  if (gameState === "final") {
    const winner = scores[0] > scores[1] ? 0 : scores[1] > scores[0] ? 1 : null;
    return (
      <div style={{textAlign:"center",padding:"16px 0"}}>
        <div style={{fontSize:52,marginBottom:8}}>{winner===null?"🤝":"🏆"}</div>
        <p style={{fontFamily:"'DM Serif Display', serif",fontSize:"1.7rem",color:winner!==null?colors[winner]:"var(--text-color)",margin:"0 0 8px"}}>{winner===null?"It's a draw!":`${names[winner]} wins!`}</p>
        <div style={{display:"flex",gap:16,justifyContent:"center",marginBottom:24,flexWrap:"wrap"}}>
          {[0,1].map(p=>(
            <div key={p} style={{background:p===0?"var(--primary-light)":"var(--secondary-light)",borderRadius:"var(--radius-lg)",padding:"20px 28px",minWidth:140}}>
              <div style={{fontSize:"2.2rem",fontWeight:800,color:colors[p],fontFamily:"'DM Serif Display', serif"}}>{scores[p]}</div>
              <div style={{fontWeight:700,color:colors[p]}}>{names[p]}</div>
              {winner===p&&<div style={{fontSize:"1.2rem",marginTop:4}}>🏆</div>}
            </div>
          ))}
        </div>
        <div style={{maxHeight:240,overflowY:"auto",marginBottom:20,textAlign:"left"}}>
          {roundHistory.map((r,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",background:i%2===0?"var(--bg-secondary)":"transparent",borderRadius:"var(--radius-sm)",fontSize:"0.8rem"}}>
              <span style={{minWidth:20,color:"var(--text-muted)",fontWeight:700}}>{i+1}.</span>
              <span style={{flex:1,color:"var(--text-color)"}}>{r.q}</span>
              <span style={{fontWeight:700,color:r.winner===0?colors[0]:r.winner===1?colors[1]:"var(--text-muted)"}}>{r.winner===null?"⏱ Time":r.winner===0?`🟦 ${names[0]}`:`🟥 ${names[1]}`}</span>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"center"}}>
          <button className="btn primary-btn" onClick={startGame}>Rematch! ⚔️</button>
          <button className="btn secondary-btn" onClick={()=>setGameState("lobby")}>Change Players</button>
        </div>
      </div>
    );
  }
  if (!q) return null;
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:8,alignItems:"center",marginBottom:16}}>
        <div style={{background:"var(--primary-light)",borderRadius:"var(--radius-lg)",padding:"12px 16px",textAlign:"center",border:answered?.player===0?"3px solid var(--primary-color)":"3px solid transparent",transition:"border 0.2s"}}>
          <div style={{fontSize:"1.8rem",fontWeight:800,color:"var(--primary-color)",fontFamily:"'DM Serif Display', serif"}}>{scores[0]}</div>
          <div style={{fontWeight:700,color:"var(--primary-color)",fontSize:"0.85rem"}}>{names[0]}</div>
          <div style={{fontSize:"0.65rem",color:"var(--text-muted)"}}>A / S / D</div>
        </div>
        <div style={{textAlign:"center"}}>
          <div style={{fontFamily:"'DM Serif Display', serif",fontSize:"2rem",fontWeight:800,color:timeLeft<=5?"#e53e3e":"var(--text-color)"}}>{timeLeft}</div>
          <div style={{fontSize:"0.65rem",color:"var(--text-muted)",fontWeight:700}}>ROUND {roundIdx+1}/{ROUNDS}</div>
        </div>
        <div style={{background:"var(--secondary-light)",borderRadius:"var(--radius-lg)",padding:"12px 16px",textAlign:"center",border:answered?.player===1?"3px solid var(--secondary-color)":"3px solid transparent",transition:"border 0.2s"}}>
          <div style={{fontSize:"1.8rem",fontWeight:800,color:"var(--secondary-color)",fontFamily:"'DM Serif Display', serif"}}>{scores[1]}</div>
          <div style={{fontWeight:700,color:"var(--secondary-color)",fontSize:"0.85rem"}}>{names[1]}</div>
          <div style={{fontSize:"0.65rem",color:"var(--text-muted)"}}>← / ↓ / →</div>
        </div>
      </div>
      <div style={{height:6,background:"var(--border-color)",borderRadius:3,marginBottom:20,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${(timeLeft/ROUND_TIME)*100}%`,borderRadius:3,transition:"width 1s linear",background:timeLeft<=5?"#e53e3e":timeLeft<=10?"var(--accent-color)":"var(--secondary-color)"}}/>
      </div>
      <div style={{textAlign:"center",fontSize:"0.75rem",fontWeight:700,color:"var(--text-muted)",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.05em"}}>{q.cat}</div>
      <div style={{textAlign:"center",fontFamily:"'DM Serif Display', serif",fontSize:"clamp(1.1rem,2.5vw,1.5rem)",color:"var(--text-color)",marginBottom:20,lineHeight:1.4,minHeight:56,padding:"0 8px"}}>{q.q}</div>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
        {q.opts.map((opt, oi) => {
          const isCorrect = oi === q.ans;
          const p1picked = answered?.player===0 && answered?.choice===oi;
          const p2picked = answered?.player===1 && answered?.choice===oi;
          let bg = "var(--bg-secondary)";
          let border = "1.5px solid var(--border-color)";
          if (answered) {
            if (isCorrect) { bg = "var(--secondary-light)"; border = "2px solid var(--secondary-color)"; }
            else if (p1picked || p2picked) { bg = "#fef2f2"; border = "2px solid #e53e3e"; }
          }
          return (
            <div key={oi} style={{display:"grid",gridTemplateColumns:"32px 1fr 32px",alignItems:"center",gap:8,background:bg,border,borderRadius:"var(--radius-md)",padding:"12px 14px",transition:"all 0.25s"}}>
              <div style={{background:"var(--primary-light)",borderRadius:"var(--radius-sm)",padding:"4px 0",textAlign:"center",fontSize:"0.7rem",fontWeight:700,color:"var(--primary-color)"}}>
                {optionKeys[0][oi]}{p1picked && <div style={{fontSize:"0.65rem"}}>{answered.correct?"✅":"❌"}</div>}
              </div>
              <div style={{textAlign:"center",fontWeight:600,color:"var(--text-color)",fontSize:"0.95rem"}}>
                {opt}{answered && isCorrect && <span style={{marginLeft:8,color:"var(--secondary-color)"}}>✓</span>}
              </div>
              <div style={{background:"var(--secondary-light)",borderRadius:"var(--radius-sm)",padding:"4px 0",textAlign:"center",fontSize:"0.7rem",fontWeight:700,color:"var(--secondary-color)"}}>
                {optionKeys[1][oi]}{p2picked && <div style={{fontSize:"0.65rem"}}>{answered.correct?"✅":"❌"}</div>}
              </div>
            </div>
          );
        })}
      </div>
      {answered && (
        <div style={{textAlign:"center",padding:"10px",borderRadius:"var(--radius-md)",marginTop:8,background:answered.player===null?"var(--bg-secondary)":answered.correct?"var(--secondary-light)":"#fef2f2",border:`1.5px solid ${answered.player===null?"var(--border-color)":answered.correct?"var(--secondary-color)":"#e53e3e"}`}}>
          <span style={{fontWeight:700,fontSize:"0.95rem",color:answered.player===null?"var(--text-muted)":answered.correct?"var(--secondary-color)":"#e53e3e"}}>
            {answered.player===null ? "⏱ Time's up! No one scored." : answered.correct ? `🎉 ${names[answered.player]} got it! +2 pts` : `❌ ${names[answered.player]} was wrong! −1 pt. Next up…`}
          </span>
        </div>
      )}
      {!answered && (<div style={{textAlign:"center",fontSize:"0.8rem",color:"var(--text-muted)"}}>🟦 {names[0]}: <strong>A / S / D</strong> &nbsp;&nbsp; 🟥 {names[1]}: <strong>← / ↓ / →</strong></div>)}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function Resources() {
  const navigate = useNavigate();

  const [theme, setTheme]               = useState(localStorage.getItem("theme") || "light-theme");
  const [showInsightMenu, setShowInsightMenu] = useState(false);
  const hideTimeout = useRef(null);

  const [activeSection, setActiveSection] = useState("stories");
  const [activeGame, setActiveGame]       = useState(null);
  const [showSoulScape, setShowSoulScape] = useState(false);

  const [userAnswers, setUserAnswers] = useState(Array(10).fill(""));
  const [score,       setScore]       = useState(0);
  const [showResult,  setShowResult]  = useState(false);

  const [breathPhase,    setBreathPhase]    = useState("idle");
  const [breathInterval, setBreathInterval] = useState(null);

  const [gratitudes,   setGratitudes]   = useState([]);
  const [gratInput,    setGratInput]    = useState("");
  const [gratLoading,  setGratLoading]  = useState(false);

  useEffect(() => { document.body.className = theme; }, [theme]);

  const toggleTheme = () => {
    const themes = ["light-theme", "dark-theme", "zen-theme"];
    const nextTheme = themes[(themes.indexOf(theme) + 1) % themes.length];
    setTheme(nextTheme);
    localStorage.setItem("theme", nextTheme);
    document.body.className = nextTheme;
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const handleInsightMouseEnter = () => { clearTimeout(hideTimeout.current); setShowInsightMenu(true); };
  const handleInsightMouseLeave = () => { hideTimeout.current = setTimeout(() => setShowInsightMenu(false), 150); };

  const startBreath = () => {
    if (breathInterval) return;
    const seq=["inhale","hold","exhale","hold"]; let i=0; setBreathPhase(seq[0]);
    const t=setInterval(()=>{i=(i+1)%4;setBreathPhase(seq[i]);},4000);
    setBreathInterval(t);
  };
  const stopBreath = () => { clearInterval(breathInterval); setBreathInterval(null); setBreathPhase("idle"); };
  useEffect(() => () => clearInterval(breathInterval), [breathInterval]);

  useEffect(() => {
    if (!auth.currentUser) return;
    const uid    = auth.currentUser.uid;
    const colRef = collection(db, "gratitudeEntries", uid, "entries");
    const cutoff = Timestamp.fromMillis(Date.now() - 24 * 60 * 60 * 1000);

    onSnapshot(query(colRef, where("createdAt", "<", cutoff)), (snap) => {
      snap.docs.forEach((d) => deleteDoc(d.ref));
    });

    const unsub = onSnapshot(query(colRef, where("createdAt", ">=", cutoff)), (snap) => {
      setGratitudes(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis())
      );
    });
    return unsub;
  }, [auth.currentUser?.uid]);

  const addGratitude = async () => {
    const wordCount = gratInput.trim().split(/\s+/).filter(Boolean).length;
    if (!gratInput.trim() || wordCount > 100 || !auth.currentUser) return;
    setGratLoading(true);
    try {
      await addDoc(collection(db, "gratitudeEntries", auth.currentUser.uid, "entries"), {
        text:      gratInput.trim(),
        createdAt: Timestamp.now(),
      });
      setGratInput("");
    } catch (err) {
      console.error(err);
    } finally {
      setGratLoading(false);
    }
  };

  const deleteGratitude = async (id) => {
    if (!auth.currentUser) return;
    await deleteDoc(doc(db, "gratitudeEntries", auth.currentUser.uid, "entries", id));
  };

  const ALL_GAMES = [
    {id:"ttt",      emoji:"❌⭕", title:"Tic Tac Toe",   desc:"Play against a beatable AI. Can you win?"},
    {id:"memory",   emoji:"🃏",   title:"Memory Match",  desc:"Flip cards and find all matching pairs."},
    {id:"guess",    emoji:"🔢",   title:"Number Guess",  desc:"Guess the secret number 1-100."},
    {id:"scramble", emoji:"🔤",   title:"Word Scramble", desc:"Unscramble words across 20 levels!"},
    {id:"snake",    emoji:"🐍",   title:"Snake",         desc:"Classic snake — eat apples, don't crash!"},
    {id:"reaction", emoji:"⚡",   title:"Reaction Test", desc:"How fast can you click when it turns green?"},
    {id:"math",     emoji:"🧮",   title:"Math Sprint",   desc:"40 levels of mental math against the clock!"},
    {id:"duel",     emoji:"⚔️",   title:"2-Player Duel", desc:"Trivia battle on the same keyboard — first to buzz wins!"},
  ];

  function handleNavClick(id) {
    if (id === "soulscape") { setShowSoulScape(true); return; }
    if (id === "roadmap") { navigate("/roadmap"); return; }
    setActiveSection(id);
    if (id !== "games") setActiveGame(null);
  }

  return (
    <div className="dashboard-container">

      {showSoulScape && <SoulScapeOverlay onClose={() => setShowSoulScape(false)} />}

      <nav className="navbar">
        <div className="navbar-left">
          <Link to="/dashboard" className="nav-item">Home</Link>
          <Link to="/resources" className="nav-item active">Resources</Link>
          <Link to="/community">Community</Link>
          <Link to="/feed" className="nav-item">Feed</Link>
          <Link to="/chatbot">Chat with Saksham</Link>
          <div className="nav-dropdown" onMouseEnter={handleInsightMouseEnter} onMouseLeave={handleInsightMouseLeave}>
            <span className="nav-item">Insight ▾</span>
            {showInsightMenu && (
              <div className="dropdown-menu">
                <Link to="/insight" className="dropdown-link">See Saved Diary</Link>
                <Link to="/insight" className="dropdown-link">Look at Report</Link>
              </div>
            )}
          </div>
          <Link to="/chattr" className="nav-item">Chattr</Link>
        </div>
        <div className="navbar-center">
          <span className="healhub-brand"><span className="hug-icon">💖🌿</span> HealHub</span>
          <Link to="/about" className="nav-item">About Us</Link>
        </div>
        <div className="navbar-right">
          <button onClick={toggleTheme} className="theme-switcher-btn">
            {theme === "light-theme" ? <MdOutlineLightMode /> : theme === "dark-theme" ? <MdModeNight /> : <MdFilterVintage />}
          </button>
          <Link to="/profile" className="profile-icon-link"><FaUserCircle size={24} /></Link>
          <button onClick={handleLogout} className="logout-btn">Log Out</button>
        </div>
      </nav>

      <div className="animate-in" style={{marginBottom:32}}>
        <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:6}}>
          <div style={{width:44, height:44, borderRadius:"var(--radius-md)", background:"var(--primary-light)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.4rem", flexShrink:0}}>🌿</div>
          <div>
            <h1 style={{marginBottom:2, fontSize:"clamp(1.5rem,3vw,2rem)"}}>Wellness Toolkit</h1>
            <p style={{color:"var(--text-muted)", fontSize:"0.875rem", margin:0}}>Stories, mental health tools, games &amp; more — all in one place.</p>
          </div>
        </div>
      </div>

      <div style={{display:"flex",gap:24,alignItems:"flex-start"}}>

        {/* ── SIDEBAR ── */}
        <div style={{width:220, flexShrink:0, position:"sticky", top:88, background:"var(--card-bg-color)", border:"1px solid var(--border-color)", borderRadius:"var(--radius-xl)", padding:"12px 10px", boxShadow:"var(--card-box-shadow)"}}>
          <div style={{fontSize:"0.65rem", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--text-muted)", padding:"4px 10px 10px", marginBottom:2}}>Tools</div>
          {NAV_ITEMS.map(item => {
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                style={{display:"flex", alignItems:"center", gap:10, width:"100%", padding:"9px 11px", borderRadius:"var(--radius-md)", fontFamily:"'DM Sans', sans-serif", fontSize:"0.85rem", cursor:"pointer", transition:"all var(--transition-fast)", textAlign:"left", border:"none", background: isActive ? "var(--primary-light)" : "transparent", color: isActive ? "var(--primary-color)" : "var(--text-secondary-color)", fontWeight: isActive ? 600 : 400, position:"relative"}}
                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = "var(--bg-secondary)"; e.currentTarget.style.color = "var(--text-color)"; } }}
                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-secondary-color)"; } }}
              >
                {isActive && (<span style={{position:"absolute", left:0, top:"20%", bottom:"20%", width:3, borderRadius:99, background:"var(--primary-color)"}}/>)}
                <span style={{width:28, height:28, borderRadius:"var(--radius-sm)", background: isActive ? "var(--primary-color)" : "var(--bg-secondary)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.85rem", flexShrink:0, transition:"background var(--transition-fast)"}}>{item.emoji}</span>
                <span style={{flex:1}}>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* ── CONTENT AREA ── */}
        <div style={{flex:1,minWidth:0}}>

          {activeSection === "stories" && (
            <SectionCard title="📚 Community Stories"><StoryWriter /></SectionCard>
          )}

          {activeSection === "breathing" && (
            <SectionCard title="🌬️ Guided Breathing">
              <p style={{marginBottom:32,color:"var(--text-muted)"}}>Box breathing: inhale 4s → hold 4s → exhale 4s → hold 4s. Repeat to calm your nervous system.</p>
              <div style={{textAlign:"center"}}>
                <div className="breathing-circle" style={{background:breathPhase==="inhale"?"var(--primary-light)":breathPhase==="hold"?"var(--secondary-light)":breathPhase==="exhale"?"var(--accent-light)":"var(--bg-secondary)",color:breathPhase==="inhale"?"var(--primary-color)":breathPhase==="hold"?"var(--secondary-color)":breathPhase==="exhale"?"var(--accent-color)":"var(--text-muted)",animation:breathPhase!=="idle"?"breathe 8s infinite ease-in-out":"none",fontWeight:700,fontFamily:"'DM Sans', sans-serif"}}>
                  {breathPhase==="idle"?"Ready":breathPhase==="inhale"?"Inhale…":breathPhase==="hold"?"Hold…":"Exhale…"}
                </div>
                {breathPhase==="idle" ? <button className="btn primary-btn" onClick={startBreath}>Start Breathing</button> : <button className="btn secondary-btn" onClick={stopBreath}>Stop</button>}
              </div>
            </SectionCard>
          )}

          {activeSection === "challenge" && (
            <SectionCard title="🎯 Daily Challenge">
              <DailyQuoteCard />
              <DailyChallenge />
            </SectionCard>
          )}

          {activeSection === "gratitude" && (
            <SectionCard title="🙏 Gratitude Wall">
              <p style={{marginBottom:16,color:"var(--text-muted)"}}>Write what you're grateful for today. Entries disappear after 24 hours. 🌅</p>
              <div style={{marginBottom:6,maxWidth:500}}>
                <textarea placeholder="I'm grateful for…" value={gratInput} onChange={(e) => setGratInput(e.target.value)} rows={3} style={{width:"100%",borderRadius:"var(--radius-md)",border:"1.5px solid var(--border-color)",padding:"10px 14px",fontSize:"0.9rem",fontFamily:"'DM Sans', sans-serif",resize:"none",outline:"none",boxSizing:"border-box",background:"var(--bg-secondary)",color:"var(--text-color)"}}/>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6,marginBottom:10}}>
                  <span style={{fontSize:"0.72rem",color:gratInput.trim().split(/\s+/).filter(Boolean).length>100?"#e53e3e":"var(--text-muted)"}}>{gratInput.trim()===""?0:gratInput.trim().split(/\s+/).filter(Boolean).length} / 100 words</span>
                  <button className="btn primary-btn" onClick={addGratitude} disabled={gratLoading||!gratInput.trim()||gratInput.trim().split(/\s+/).filter(Boolean).length>100} style={{padding:"7px 20px",fontSize:"0.85rem"}}>{gratLoading?"Saving…":"Add ✨"}</button>
                </div>
              </div>
              {gratitudes.length > 0 ? (
                <ul style={{padding:0,listStyle:"none",display:"flex",flexDirection:"column",gap:10}}>
                  {gratitudes.map((g) => {
                    const hoursLeft = Math.max(0,Math.floor((g.createdAt?.toMillis()+24*60*60*1000-Date.now())/3600000));
                    const minsLeft  = Math.max(0,Math.floor(((g.createdAt?.toMillis()+24*60*60*1000-Date.now())%3600000)/60000));
                    return (
                      <li key={g.id} style={{background:"var(--accent-light)",border:"1px solid var(--border-subtle)",borderLeft:"3px solid var(--accent-color)",padding:"12px 16px",borderRadius:"var(--radius-md)",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                        <div style={{flex:1}}>
                          <p style={{margin:"0 0 4px",color:"var(--text-color)",fontSize:"0.9rem"}}>🌟 {g.text}</p>
                          <p style={{margin:0,fontSize:"0.68rem",color:"var(--text-muted)"}}>⏳ {hoursLeft>0?`${hoursLeft}h ${minsLeft}m left`:`${minsLeft}m left`}</p>
                        </div>
                        <button onClick={()=>deleteGratitude(g.id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text-muted)",fontSize:"1rem",padding:"2px 6px",borderRadius:"var(--radius-sm)",transition:"color 0.2s"}} onMouseEnter={e=>e.currentTarget.style.color="#e53e3e"} onMouseLeave={e=>e.currentTarget.style.color="var(--text-muted)"}>✕</button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p style={{color:"var(--text-muted)",fontSize:"0.875rem"}}>Nothing yet — start with one thing, big or small!</p>
              )}
            </SectionCard>
          )}

          {activeSection === "exercises" && (
            <SectionCard title="💪 Guided Exercises"><Exercises /></SectionCard>
          )}

          {activeSection === "test" && <WellnessTests />}

          {activeSection === "games" && (
            <SectionCard title="🎮 Mini Games">
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:24,borderBottom:"1px solid var(--border-color)",paddingBottom:16}}>
                {ALL_GAMES.map(g=>(
                  <button key={g.id} className={`btn ${activeGame===g.id?"primary-btn":"secondary-btn"}`} onClick={()=>setActiveGame(activeGame===g.id?null:g.id)} style={{fontSize:"0.8rem",padding:"7px 13px"}}>{g.emoji} {g.title}</button>
                ))}
              </div>
              {!activeGame && (
                <div className="challenge-grid">
                  {ALL_GAMES.map(g=>(
                    <div key={g.id} className="challenge-card" style={{cursor:"pointer"}} onClick={()=>setActiveGame(g.id)}>
                      <div style={{fontSize:32,marginBottom:10}}>{g.emoji}</div>
                      <h3>{g.title}</h3>
                      <p style={{marginBottom:14}}>{g.desc}</p>
                      <button className="btn primary-btn" style={{fontSize:"0.8rem",padding:"6px 14px"}}>Play →</button>
                    </div>
                  ))}
                </div>
              )}
              {activeGame==="ttt"      && <TicTacToe />}
              {activeGame==="memory"   && <MemoryMatch />}
              {activeGame==="guess"    && <NumberGuess />}
              {activeGame==="scramble" && <WordScramble key="scramble" />}
              {activeGame==="snake"    && <SnakeGame key="snake" />}
              {activeGame==="reaction" && <ReactionTest key="reaction" />}
              {activeGame==="math"     && <MathSprint key="math" />}
              {activeGame==="duel"     && <TwoPlayerDuel key="duel" />}
            </SectionCard>
          )}

          {activeSection === "jokes" && <JokesSection />}

        </div>
      </div>
    </div>
  );
}